/**
 * API Client Layer
 *
 * Provides a thin wrapper around fetch() for all admin endpoints.
 * Handles authentication errors and response typing.
 * Supports multiple base URLs for different API sections (/api/admin, /api/settings, /api/explore).
 */

import type { Briefing, BriefingTier } from '../pages/briefings/types';
import type { HygieneScore, RosterResponse } from '../pages/my-salesforce/hygiene-shared';
import type { AlertComment, CleanupAlert, CleanupCheckpointInfo, CleanupScanRun } from '../pages/briefings/cleanup-alerts-shared';
import type {
  SuggestedQuestionsResponse,
  SuggestedQuestionsView,
} from '../pages/briefings/territory-types';

// `Connector` and `CurrentUser` live in `client/src/types/` (single source of
// truth). Imported for use within this module and re-exported so existing
// `import { Connector | CurrentUser } from '../services/api'` call sites keep
// working.
import type { Connector, CurrentUser } from '../types';
// Compass App (mobile) device-session shapes — shared with the server (type-only).
import type { DeviceSessionListResponse } from '@compass/contracts/mobile-auth';
import type { AdminUserConnector } from '../types/user-connectors';
import type { PersonalSpacePurgeResult, PersonalSpaceSource, PersonalSpaceSourceSummary } from '../types/personal-space';
import type { PersonalItemDetailResponse, PersonalItemsQuery, PersonalItemsResponse, PersonalOverview, PersonalPeopleResponse } from '@compass/contracts/personal-space';
// Page-view analytics (Admin Settings → Usage) — shared with the server (type-only).
import type { ProgressResponse } from '@compass/contracts/progress';
import type {
  BenchmarkCaseRow, BenchmarkCaseSpecInput, BenchmarkCompareResponse, BenchmarkConfig, BenchmarkEstimate, BenchmarkFixturesStatus, BenchmarkOverview, BenchmarkPersonaRef,
  BenchmarkResultDetail, BenchmarkResultRow, BenchmarkRunDetail, BenchmarkRunRequest, BenchmarkRunRow, BenchmarkSuite, ImportCandidate, UsageWeightsResponse,
} from '@compass/contracts/benchmark';
import type { PageViewDailyQuery, PageViewDailyResponse, PageViewReportQuery, PageViewScope, PageViewStatsQuery, PageViewStatsResponse, UserPageViewsResponse } from '@compass/contracts/page-views';
// The approval queue ("Needs you") — shared with the server and the Compass App (type-only).
import type { ApprovalCountResponse, ApprovalListResponse, ApprovalQueueFilter, ApprovalResponse, ApprovalView } from '@compass/contracts/approvals';
import type { NotificationCountResponse, NotificationItemView, NotificationListResponse, NotificationReadAllResponse, NotificationReadResponse, SmsConsentResponse, SmsConsentView } from '@compass/contracts/notifications';
import type { CalendarCreateEventRequest, CalendarCreateEventResponse, CalendarEventMeetingResponse, CalendarEventsResponse } from '@compass/contracts/calendar';
export type { Connector, CurrentUser } from '../types';

/** Fired instead of the /login redirect while a recording holds it (see `holdLoginRedirect`). */
export const SESSION_EXPIRED_EVENT = 'compass:session-expired';
let loginRedirectHolds = 0;
/** While held, a 401 does not navigate to /login (which would end a recording) — it fires SESSION_EXPIRED_EVENT. Returns the release. */
export function holdLoginRedirect(): () => void {
  loginRedirectHolds += 1;
  let done = false;
  return () => { if (!done) { done = true; loginRedirectHolds -= 1; } };
}

// ── API Response Types ──

export interface ApiError {
  /** HTTP status the server put in the body (every `createApiError` body has it). */
  statusCode?: number;
  error: string;
  /** Human text (registry actions and `createApiError`). */
  message?: string;
  code?: string;
  details?: unknown;
}

/**
 * Generic data response wrapper with pagination metadata.
 * Used by data exploration endpoints to provide consistent response structure.
 */
export interface DataResponse<T> {
  data: T[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
    capped?: boolean;
  };
}

// ── Accounts (CO-437) ──

/** One row of the Accounts list — all money/count fields are true server-side
 *  totals (never page-scoped). Mirrors the server `AccountListRow`. */
export interface AccountListRow {
  sfId: string;
  name: string;
  region: string | null;
  aeName: string | null;
  aeSfId: string | null;
  rvpName: string | null;
  /** Salesforce Account.Clinical_Success_Consultant__c (a User), when set. */
  clinicalSuccessConsultantName: string | null;
  /** That consultant's Salesforce user id — the `csc` filter value. */
  clinicalSuccessConsultantSfId: string | null;
  openAcv: number | null;
  commitAcv: number | null;
  openOppCount: number;
  riskFlagCount: number;
  lastActivityAt: string | null;
  /** Combined data hygiene score (range 4–9, 9 = perfect). */
  hygieneScore: number;
}

export interface AccountsMeta {
  source: 'salesforce';
  syncedAt: string | null;
  scope: 'leadership' | 'scoped';
  /** When false the viewer can't see dollar values: every money field in the
   *  response is null and the money UI is hidden. */
  canSeeFinancials: boolean;
}

/** Distinct filter option lists for the list-page dropdowns. Mirrors the
 *  server `AccountsFacets`; derived over the viewer's full in-scope set so the
 *  dropdowns stay stable as you filter. */
export interface AccountsFacets {
  regions: string[];
  aes: Array<{ sfId: string; name: string }>;
  rvps: string[];
  /** Clinical Success Consultants (sfId = the `csc` filter value). */
  cscs: Array<{ sfId: string; name: string }>;
}

export interface AccountsListResponse {
  data: AccountListRow[];
  pagination: { total: number; limit: number; offset: number; hasMore: boolean };
  facets: AccountsFacets;
  meta: AccountsMeta;
}

export interface AccountsListParams {
  offset?: number;
  limit?: number;
  search?: string;
  region?: string;
  ae?: string;
  rvp?: string;
  /** Clinical Success Consultant's Salesforce user id. */
  csc?: string;
  risk?: 'has' | 'clear' | 'any';
  sort?: 'name' | 'openAcv' | 'commit' | 'openOpps' | 'lastActivity';
  sortOrder?: 'asc' | 'desc';
}

// Account detail aggregate (CO-439/CO-442). Mirrors the server `AccountDetail`.
export type AccountFlagKind = 'stale_notes' | 'no_in_person_90d' | 'stage_stagnation_45d' | 'close_date_pushed';
export interface AccountHeader {
  sfId: string; name: string; type: string | null; industry: string | null;
  billingCountry: string | null; website: string | null;
  region: string | null; aeName: string | null; aeSfId: string | null; rvpName: string | null;
  clinicalSuccessConsultantName: string | null;
  parentSfId: string | null;
  parentName: string | null;
}
export interface AccountScoreboard {
  openAcv: number | null; openOppCount: number; commitAcv: number | null; commitCount: number;
  bestCaseAcv: number | null; bestCaseCount: number; closedWonYtd: number | null; riskFlagCount: number;
}
export interface AccountOpportunity {
  sfId: string; name: string; stage: string | null;
  stageSince: string | null; daysInStage: number | null;
  forecastCategory: string | null;
  acv: number | null; ownerName: string | null; closeDate: string | null; flags: AccountFlagKind[];
  /** True when this open opp is a non-selling motion (renewal, evaluation, …)
   *  excluded from the account's selling KPIs (open ACV / openOppCount). It still
   *  shows in the opportunities list but isn't counted in the scoreboard. */
  excludedFromMetrics: boolean;
  /** Total CloseDate push count from SF field history (0 = never pushed). */
  closeDatePushCount: number;
}
export interface AccountProcessStep {
  name: string; targetDate: string | null; status: 'overdue' | 'upcoming' | 'done'; daysFromToday: number | null;
}
export interface AccountLeadNote { oppName: string; notes: string; updatedAt: string | null; stale: boolean; }
export interface AccountRiskFlag { kind: AccountFlagKind; firing: boolean; detail: string; }
export interface AccountActivity { date: string | null; kind: string; subject: string | null; inPerson: boolean; }
export interface AccountContact { name: string; title: string | null; email: string | null; champion: boolean; }
export interface AccountDetail {
  account: AccountHeader;
  scoreboard: AccountScoreboard;
  opportunities: AccountOpportunity[];
  processSteps: AccountProcessStep[];
  leadNotes: AccountLeadNote[];
  riskFlags: AccountRiskFlag[];
  activities: AccountActivity[];
  contacts: AccountContact[];
  /** 'deep_dive' = Deep_Dive_Contact__c; 'legacy_sf' = fell back to SF Contacts (no DDC records). */
  contactsSource: 'deep_dive' | 'legacy_sf';
  meta: AccountsMeta;
}

// ── Accounts → Opportunity Insights enrichment ──
export interface EnrichedSite {
  siteId: string;
  accountKey: string | null;
  siteUid: string | null;
  siteName: string | null;
  siteDescription: string | null;
  city: string | null;
  country: string | null;
  mobileAppEnabled: boolean;
  ctaEnabled: boolean;
  ichEnabled: boolean;
  ctPerfusionEnabled: boolean;
}
export interface EnrichedCase {
  sfId: string;
  caseNumber: string | null;
  subject: string | null;
  status: string | null;
  priority: string | null;
  type: string | null;
  isClosed: boolean;
  openedAt: string | null;
  closedAt: string | null;
  jiraKey: string | null;
}
export interface ProductEngagement {
  totalEvents: number;
  activeUsers: number;
  activePatients: number;
  topModules: Array<{ name: string; eventCount: number }>;
  topRoles: Array<{ role: string; userCount: number }>;
  dataFrom: string | null;
  dataThrough: string | null;
}
export interface ClinicalOutcomes {
  totalPatients: number;
  avgNihss: number | null;
  tpaRate: number | null;
  mtRate: number | null;
  goodOutcomeRate: number | null;
  strokeTypes: Array<{ type: string; count: number }>;
  dataFrom: string | null;
  dataThrough: string | null;
}
export interface AccountHierarchyInfo {
  hierarchyLevel: number | null;
  ultimateParentKey: number | null;
  ultimateParentName: string | null;
  parentSfId: string | null;
  parentName: string | null;
}
export interface BeaconHealth {
  total: number;
  byStatus: Array<{ status: string; count: number }>;
  lastSeenAt: string | null;
}
export interface IncidentMetrics {
  critHighLastYear: number;
  openCritHigh: number;
  avgResolutionDays: number | null;
  siteDownCount12mo: number;
  avgSiteDownMinutes: number | null;
  /** Last 26 weeks (current one partial), oldest first; week-start ISO date. */
  weeklyTrend: Array<{ week: string; total: number; critHigh: number; siteDown: number; avgResolutionDays: number | null }>;
  /** Last 12 months, oldest first; month-start ISO date. */
  monthlyTrend: Array<{ month: string; total: number; critHigh: number; siteDown: number; avgResolutionDays: number | null }>;
  /** Last 8 quarters (current one partial), oldest first; quarter-start ISO date. */
  quarterlyTrend: Array<{ quarter: string; total: number; critHigh: number; siteDown: number; avgResolutionDays: number | null }>;
  supportTouches12mo: number;
  supportTouches30d: number;
  supportTouches31_60d: number;
}
export interface MobileUsage {
  monthlyActiveUsers: Array<{ month: string; users: number; events: number }>;
  weeklyEvents: Array<{ week: string; users: number; events: number }>;
  quarterlyEvents: Array<{ quarter: string; users: number; events: number }>;
  patientsByModule: Array<{ module: string; patients: number }>;
  patientsByFeature: Array<{ feature: string; patients: number; users: number }>;
  monthlyPatientsByFeature: Array<{ month: string; feature: string; patients: number; users: number }>;
  monthlyTotalPatients: Array<{ month: string; patients: number }>;
  totalPatients: number;
  distinctUsers12mo: number;
  activeSites12mo: number;
  activeSites3mo: number;
  moduleMonthlyEvents: Array<{ month: string; module: string; events: number; users: number }>;
  moduleWeeklyEvents: Array<{ week: string; module: string; events: number; users: number }>;
  moduleQuarterlyEvents: Array<{ quarter: string; module: string; events: number; users: number }>;
  provisionedMobileUsers: number;
}
export interface ContractedModules {
  contractStatus: string | null;
  firstSignedDate: string | null;
  modules: Array<{ module: string; contractedDate: string | null }>;
}
export interface ClinicalTrialSite {
  id: string;
  hospitalName: string;
  projectStage: string | null;
  siteType: string | null;
  commercialOpportunity: string | null;
  contractedModules: string | null;
  modulesUtilized: string | null;
  rapidAiActiveDate: string | null;
  currentSalesStatus: string | null;
}

export interface ClinicalStudy {
  id: string;
  studyName: string;
  institutionName: string | null;
  investigatorName: string | null;
  studyType: string | null;
  modules: string | null;
  diseaseState: string | null;
  expectedFinancialValue: string | null;
  stage: string | null;
}

export interface ClinicalTrial {
  id: string;
  trialName: string;
  stage: string | null;
  subStatus: string | null;
  modules: string | null;
  diseaseState: string | null;
  contractValue: string | null;
  projectLead: string | null;
}

export interface SalesOpp {
  sfId: string;
  name: string;
  stage: string | null;
  acv: number | null;
  closeDate: string | null;
  forecastCategory: string | null;
  daysInStage: number | null;
  nextStep: string | null;
  closeDatePushCount: number;
  isRenewal: boolean;
  /** Oppty_Was_At_Risk__c on the opportunity. */
  atRisk: boolean;
}
export type AtRiskSource = 'opportunity' | 'account_score';
export interface SalesIndicators {
  openOppCount: number;
  openOppTotalAcv: number | null;
  openOpps: SalesOpp[];
  /** An open opp flagged Oppty_Was_At_Risk__c OR Account_Score__c = 'Intervene'. */
  atRisk: boolean;
  /** Which source(s) made atRisk true — empty when false. */
  atRiskSources: AtRiskSource[];
  /** Open opps flagged Oppty_Was_At_Risk__c (up to 5). */
  atRiskOpps: Array<{ sfId: string; name: string }>;
  healthStatus: string | null;
  lastActivityDate: string | null;
  lastActivityType: string | null;
  touchCount30d: number;
  touchCount31_60d: number;
  /** Account_Champion__c contact first ('account_champion'), then Authority__c / Stakeholder_Category__c contacts ('contact_field'). */
  /** `unsynced` = Account_Champion__c names a contact that isn't synced (name null). */
  champions: Array<{ name: string | null; title: string | null; email: string | null; source: 'account_champion' | 'contact_field'; unsynced: boolean }>;
  contractEndDate: string | null;
  daysToRenewal: number | null;
  renewalSource: 'sf_opp' | 'snowflake' | null;
  /** QBR activities + the Account's QBR_Completed_Date__c (same day counted once). */
  qbrCount12mo: number;
  recentQbrDates: string[];
  /** Account Next_QBR_Date__c. */
  nextQbrDate: string | null;
  /** Visit activities + the Account's Most_Recent_Visited_Site__c (same day counted once). */
  siteVisitCount12mo: number;
  lastSiteVisitDate: string | null;
  recentSiteVisitDates: string[];
  championCount: number;
  totalContacts: number;
  physicianTurnoverCount: number;
  kolCount: number;
  kolContacts: Array<{ name: string; title: string | null; email: string | null }>;
  p2pCount12mo: number;
  recentP2pDates: string[];
}
export interface AIClinicalUsage {
  sites: Array<{
    siteId: string;
    totalPatients: number;
    tpaRate: number | null;
    mtRate: number | null;
    topModules: Array<{ module: string; patients: number }>;
  }>;
  totalSitesWithData: number;
}
export interface ScanSuspectedRates {
  ichSuspectedRate: number | null;
  ichTotalScans: number;
  ichSuspectedScans: number;
  lvoSuspectedRate: number | null;
  lvoTotalScans: number;
  lvoSuspectedScans: number;
}

export interface ScanSuspectedTrend {
  months: Array<{
    month: string;
    ichTotal: number;
    ichSuspected: number;
    lvoTotal: number;
    lvoSuspected: number;
  }>;
}

export interface TurnaroundTime {
  medianMinutes: number | null;
  p90Minutes: number | null;
  totalVisits: number;
  visitsWithTat: number;
  bySite: Array<{ siteId: string; medianMinutes: number; p90Minutes: number; visitCount: number }>;
  monthly: Array<{ month: string; medianMinutes: number; visitCount: number }>;
  percentileCurve: Array<{ percentile: number; minutes: number }>;
}

/**
 * Keys of the enrichment sections that can be reported as degraded. Mirrors
 * `EnrichmentSection` in `src/accounts/types.ts`.
 */
export type EnrichmentSection =
  | 'sites'
  | 'supportTickets'
  | 'productEngagement'
  | 'clinicalOutcomes'
  | 'hierarchy'
  | 'beaconHealth'
  | 'incidentMetrics'
  | 'mobileUsage'
  | 'contractedModules'
  | 'salesIndicators'
  | 'aiClinicalUsage'
  | 'scanSuspectedRates'
  | 'scanSuspectedTrend'
  | 'turnaroundTime'
  | 'researchEngagement';

export interface AccountEnrichment {
  sites: EnrichedSite[];
  supportTickets: {
    openCritHighCount: number;
    trailingTwelveMonthCount: number;
    cases: EnrichedCase[];
  };
  productEngagement: ProductEngagement;
  clinicalOutcomes: ClinicalOutcomes;
  hierarchy: AccountHierarchyInfo | null;
  beaconHealth: BeaconHealth;
  incidentMetrics: IncidentMetrics;
  mobileUsage: MobileUsage;
  contractedModules: ContractedModules;
  salesIndicators: SalesIndicators;
  aiClinicalUsage: AIClinicalUsage;
  scanSuspectedRates: ScanSuspectedRates;
  scanSuspectedTrend: ScanSuspectedTrend;
  turnaroundTime: TurnaroundTime | null;
  /** Live-queried Monday.com research engagement data for this account.
   *  null when the token is absent or the fetch fails (graceful degradation). */
  researchEngagement: {
    trialSites: ClinicalTrialSite[];
    studies: ClinicalStudy[];
    trials: ClinicalTrial[];
  } | null;
  /**
   * Sections whose data is incomplete because a warehouse query failed, not
   * because the account has no data. Render these as "temporarily unavailable"
   * — the normal empty state asserts something we don't know.
   */
  degraded: EnrichmentSection[];
  /** False when hide_financials is on or the viewer lacks financial access — the server then nulls every
   *  dollar figure: salesIndicators.openOppTotalAcv, openOpps[].acv and researchEngagement.trials[].contractValue. */
  canSeeFinancials: boolean;
}

export interface AccountCleanupAlertPatch {
  status?: string;
  severity?: string;
}

// ── Fleet Health ──
export interface FleetSupportTickets {
  totalOpen: number;
  openCritHigh: number;
  trailingTwelveMonthCount: number;
  byPriority: Array<{ priority: string; count: number }>;
  byStatus: Array<{ status: string; count: number }>;
}
export interface UrgentTicket {
  sfId: string;
  caseNumber: string | null;
  subject: string | null;
  status: string | null;
  priority: string | null;
  accountName: string | null;
  accountSfId: string | null;
}
/**
 * How much of the 12-month window a fleet monthly series really covers, after
 * dropping stub months (volume under 2% of the series median). Mirrors
 * `SeriesCoverage` in `src/fleet-health/series-coverage.ts`.
 */
export interface SeriesCoverage {
  /** First real month inside the window, `YYYY-MM-01`; null when none. */
  since: string | null;
  /** Real months with data inside the window. */
  months: number;
  /** True when real data starts at the window's first month. */
  fullWindow: boolean;
  /** Leading stub months dropped before the feed started, `YYYY-MM-01`. */
  excludedMonths: string[];
  /** Months in the window after the feed started with almost no data — kept, shown as a warning (likely a feed gap). */
  lowMonths: string[];
}
export interface FleetMobileUsage {
  totalPatients: number;
  totalVisits: number;
  activeSites: number;
  rmaMonthly: Array<{ month: string; visits: number; patients: number }>;
  mobileMonthly: Array<{ month: string; users: number; events: number }>;
  mobileMonthlyConsistent: Array<{ month: string; users: number; events: number }>;
  /** All-time distinct user counts per app feature (Viewer / Go / Com). */
  featureUsers: Array<{ feature: string; users: number; patients: number }>;
  /** Window coverage of the RMA series — and so of the "(12 mo)" RMA totals. */
  rmaCoverage: SeriesCoverage;
  /** Window coverage of the all-sites mobile series. */
  mobileCoverage: SeriesCoverage;
}
/** Fleet TAT: the account-shaped TAT without the per-site breakdown, plus its window coverage. Mirrors `FleetTurnaroundTime` in `src/fleet-health/service.ts`. */
export type FleetTurnaroundTime = Omit<TurnaroundTime, 'bySite'> & { coverage: SeriesCoverage };
/** Fleet-health sections that can be reported as degraded. Mirrors `FleetSection` in `src/fleet-health/service.ts`. */
export type FleetSection = 'supportTickets' | 'incidents' | 'mobileUsage' | 'tat' | 'scanRates' | 'scanTrend';
export interface FleetHealthData {
  supportTickets: FleetSupportTickets;
  incidents: IncidentMetrics;
  mobileUsage: FleetMobileUsage;
  tat: FleetTurnaroundTime | null;
  scanRates: ScanSuspectedRates;
  scanTrend: ScanSuspectedTrend;
  /**
   * Sections whose query failed — their figures are empty placeholders. Render
   * them as "temporarily unavailable" with no score, never as "no data".
   */
  degraded: FleetSection[];
  /** When the server next re-queries the degraded sections (ISO); null when none. A retry before then returns the same placeholder. */
  nextRetryAt: string | null;
}

export interface ModuleVolume { module: string; count: number }
export interface TatByAccount {
  accountKey: number;
  accountName: string;
  medianMinutesPrior8Weeks: number | null;
  medianMinutesLast7Days: number | null;
  deltaSecs: number | null;
}
export interface WeeklyTatByModule { week: string; module: string; medianMinutes: number }
export interface SuccessBoardData {
  moduleVolumes: ModuleVolume[];
  scanRates: ScanSuspectedRates;
  tatByAccount: TatByAccount[];
  weeklyTatByModule: WeeklyTatByModule[];
}

export interface TerritoryHospital {
  accountKey: number;
  name: string;
  ultimateParent: string | null;
  territory: string | null;
  csm: string | null;
  eae: string | null;
  contractStatus: string | null;
  rma: boolean;
  ich: boolean;
  ctPerfusion: boolean;
  hyperdensity: boolean;
  cta: boolean;
}

export interface TerritoryRepSummary {
  csm: string;
  total: number;
  rmaCount: number;
  ichCount: number;
  ctPerfusionCount: number;
  hyperdensityCount: number;
  fullSuiteCount: number;
}

export interface TerritoryAnalysisData {
  hospitals: TerritoryHospital[];
  summary: TerritoryRepSummary[];
  totalHospitals: number;
  /** Mirror freshness (newest `_ingested_at`), or null when unreadable. */
  asOf: string | null;
}

export interface AccountCleanupAlertsResponse {
  alerts: Array<Record<string, unknown>>;
}

// ── Accounts → Snowflake source tab ──
// An INDEPENDENT account browse surface keyed on the Snowflake Int64
// `accountKey`, reading the ClickHouse `wh_snowflake.*` mirror. NO cross-source
// join with the Salesforce tab. Mirrors the server `Snowflake*` types in
// src/accounts/types.ts. Reachable only by unrestricted principals.

export interface SnowflakeAccountsMeta {
  source: 'snowflake';
  syncedAt: string | null;
  scope: 'leadership';
  /** When false every dollar field is null and the money UI is hidden. */
  canSeeFinancials: boolean;
}

export interface SnowflakeAccountRow {
  accountKey: number;
  name: string;
  billingCity: string | null;
  billingState: string | null;
  billingCountry: string | null;
  contractStatus: string | null;
  salesOwner: string | null;
  atmName: string | null;
  siteCount: number;
  /** Σ annual contract value; null when the viewer can't see financials.
   *  Usually null — Snowflake populates amount_year on few contracts. */
  acv: number | null;
  /** Σ total contract value (grand_total); null when the viewer can't see
   *  financials. Far more widely populated than acv. */
  grandTotal: number | null;
}

/** Distinct filter option lists for the Snowflake list dropdowns, over the
 *  whole account base (not narrowed by the current selection). */
export interface SnowflakeAccountsFacets {
  contractStatuses: string[];
  countries: string[];
  states: string[];
}

export interface SnowflakeAccountsListResponse {
  data: SnowflakeAccountRow[];
  pagination: { total: number; limit: number; offset: number; hasMore: boolean };
  facets: SnowflakeAccountsFacets;
  meta: SnowflakeAccountsMeta;
}

export interface SnowflakeAccountsListParams {
  offset?: number;
  limit?: number;
  search?: string;
  contractStatus?: string;
  country?: string;
  state?: string;
  city?: string;
  sort?: 'name' | 'acv' | 'grandTotal' | 'siteCount' | 'contractStatus';
  sortOrder?: 'asc' | 'desc';
}

export interface SnowflakeAccountHeader {
  accountKey: number;
  name: string;
  contractManagerName: string | null;
  atmName: string | null;
  implementationManagerName: string | null;
  salesOwner: string | null;
  billingCity: string | null;
  billingState: string | null;
  billingCountry: string | null;
  contractStatus: string | null;
  mobileAppContractedDate: string | null;
  contractFirstSignedDate: string | null;
}

export interface SnowflakeModuleTimelineItem {
  module: string;
  contractedDate: string | null;
}

export interface SnowflakeSiteItem {
  isvSiteId: string;
  siteCode: string | null;
  siteDescription: string | null;
  rapidSiteId: string | null;
}

export interface SnowflakeFeatureFlag {
  isvSiteId: string;
  siteCode: string | null;
  featureName: string;
  active: boolean;
  startDate: string | null;
  endDate: string | null;
}

export interface SnowflakeContractItem {
  contractKey: number;
  status: string | null;
  products: string | null;
  startDate: string | null;
  endDate: string | null;
  salesOwner: string | null;
  region: string | null;
  termDuration: number | null;
  amountYear: number | null;
  grandTotal: number | null;
}

export interface SnowflakeAccountDetail {
  account: SnowflakeAccountHeader;
  moduleTimeline: SnowflakeModuleTimelineItem[];
  sites: SnowflakeSiteItem[];
  featureFlags: SnowflakeFeatureFlag[];
  contracts: SnowflakeContractItem[];
  meta: SnowflakeAccountsMeta;
}

// ── Parent Account types ───────────────────────────────────────────────────────

/** One row from GET /api/actions/list_parent_accounts */
export interface ParentAccountRow {
  sfId: string;
  name: string;
  childCount: number;
  aeName: string | null;
  aeSfId: string | null;
  region: string | null;
  openOppCount: number;
  openAcv: number | null;
  commitAcv: number | null;
  lastActivityAt: string | null;
}

export interface ParentAccountFacets {
  regions: string[];
  aes: { sfId: string; name: string }[];
  /** Clinical Success Consultants on the parents or their children (sfId = the `csc` filter value). */
  cscs: { sfId: string; name: string }[];
}

// ── CS KPIs (docs/Features/CsKpis) — mirrors src/cs-kpis/types.ts ──────────────

export type CsKpiScope = { kind: 'portfolio' } | { kind: 'account'; sfId: string } | { kind: 'parent'; sfId: string };
export interface CsFiscalQuarter { key: string; label: string; start: string; end: string }
export interface CsKpiRatio { count: number; of: number; pct: number | null }
export interface CsKpiTarget { value: number; direction: 'max' | 'min' }
export type CsAccountScore = 'Intervene' | 'Nurture' | 'Advocate' | 'Expand' | 'Unknown';
export interface CsKpisResponse {
  scope: CsKpiScope;
  unit: 'ultimate-parent' | 'parent' | 'account';
  quarter: CsFiscalQuarter;
  quarters: CsFiscalQuarter[];
  csc: string | null;
  cscs: Array<{ sfId: string; name: string }>;
  canSeeFinancials: boolean;
  accountCount: number;
  top10Units: number;
  businessReview: {
    completed: CsKpiRatio;
    radiologyOrNeurosurgery: CsKpiRatio;
    nextSteps: CsKpiRatio;
    onsiteCompliance: CsKpiRatio;
    onsiteTarget: CsKpiTarget;
    fieldsNewSince: string;
  };
  churn: {
    mode: 'acv' | 'count';
    lostCount: number;
    closedCount: number;
    /** Absent when financials are hidden. */
    lostAcv?: number;
    closedAcv?: number;
    pct: number | null;
    target: CsKpiTarget;
  };
  atRisk: { atRisk: CsKpiRatio; target: CsKpiTarget; scores: Record<CsAccountScore, number> };
  rma: {
    accounts: number;
    withUsageData: number;
    activeAccounts3mo: number;
    activeSites3mo: number;
    activeSites12mo: number;
    activeUsers12mo: number;
    activePatients12mo: number;
    computedAt: string | null;
  };
}
export interface CsKpisParams {
  csc?: string;
  /** `YYYY-Qn` (Salesforce Feb–Jan fiscal year); omit for the current quarter. */
  fiscalQuarter?: string;
  accountId?: string;
  parentAccountId?: string;
}

/** Response from GET /api/actions/parent_account_detail?sfId= */
export interface ParentAccountDetail {
  sfId: string;
  name: string;
  childCount: number;
  childSfIds: string[];
  children: { sfId: string; name: string }[];
  grandparentSfId: string | null;
  grandparentName: string | null;
}

// `Connector` is defined in `client/src/types/` and re-exported at the top of
// this module.

/** One row from connector_sync_watermarks (CO-337). */
export interface ConnectorWatermark {
  connectorId: string;
  objectType: string;
  lastModifiedAt: string;
  lastSyncedAt: string;
}

export interface SyncHistory {
  id: string;
  connectorId: string;
  status: 'success' | 'failed' | 'pending';
  itemsProcessed: number;
  itemsFailed: number;
  startedAt: string;
  completedAt?: string;
  errorMessage?: string;
}

/**
 * Sync Job represents a single executed sync operation.
 * Why: Needed for the SyncHistoryTable component to display recent sync runs.
 */
export interface SyncJob {
  id: string;
  connectorId: string;
  status: 'success' | 'failed' | 'running' | 'cancelled';
  itemsPulled: number;
  itemsStored: number;
  startedAt: string;
  completedAt?: string;
  errorMessage?: string;
}

// ── WBR Run Monitor (CO-401) ────────────────────────────────

export type WbrRunStatus = 'in_progress' | 'completed' | 'partial' | 'failed';
export type WbrRunTrigger = 'cron' | 'page_view' | 'admin_regenerate' | 'simulator';

/** One outbound-email attempt in the WBR Email Log. */
export interface WbrEmailLogRow {
  /** BriefingRunStep id — the handle for subject/preview lookups. */
  id: string;
  runId: string;
  kind: 'briefing' | 'hygiene_report';
  /** Step vocabulary: a send attempt, not a run. */
  status: 'ok' | 'skipped' | 'failed' | 'started';
  sentAt: string;
  /** Address the relay was actually given. Null unless the send succeeded. */
  to: string | null;
  messageId: string | null;
  /** Why a skipped/failed row didn't go out. */
  reason: string | null;
  recipientSfId: string;
  recipientName: string | null;
  /** The rep's address of record — differs from `to` under audienceMode 'dev'. */
  recipientEmail: string | null;
  week: string;
  /** Where THIS send came from — 'cron' | 'admin_manual' | 'admin_regenerate'
   *  | 'script'. Distinct from `triggeredBy`, which is the run's trigger: a
   *  manual re-send of a scheduled run reads origin='admin_manual' with
   *  triggeredBy='cron'. Pre-2026-09-22 rows fall back to the run's trigger. */
  origin: string;
  triggeredBy: string;
  /** Whether this row's body can be replayed in the viewer. */
  previewAvailable: boolean;
}

export interface WbrRunListRow {
  id: string;
  jobId: string | null;
  recipientSfId: string;
  recipientName: string | null;
  recipientTier: string | null; // 'ae' | 'rvp' | 'exec', from the stored briefing
  reportDate: string; // yyyy-mm-dd
  startedAt: string;
  finishedAt: string | null;
  status: WbrRunStatus;
  triggeredBy: WbrRunTrigger;
  errorMessage: string | null;
  stepCount: number;
}

export interface WbrRunStep {
  id: string;
  step: string;
  status: 'started' | 'ok' | 'failed' | 'skipped';
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  metadata: unknown;
  errorMessage: string | null;
}

export interface WbrRunDetail extends Omit<WbrRunListRow, 'stepCount' | 'recipientName'> {
  recipientName: string | null;
  briefingJson: unknown;
  steps: WbrRunStep[];
}

// ── WBR Section SQL Playground (CO-402) ─────────────────────

export type WbrSection =
  | 'critical_actions'
  | 'deals_needing_attention'
  | 'pipeline_activity'
  | 'forecast_snapshot'
  | 'deep_dive_readiness';

export interface WbrSectionPreviewResult {
  section: WbrSection;
  recipientSfId: string;
  reportDate: string;
  scopeSize: number;
  productionSql: string;
  candidateSql: string;
  /** Production row set — shape varies per section (array for the
   *  row-heavy sections, object for the aggregates). */
  production: unknown;
  candidate: unknown;
}

/** Draft thresholds forwarded from Customization Step 1 into the Step-2 SQL preview.
 *  Any subset; the server merges it over the saved wbr.query_config. */
export type WbrQueryConfigDraft = Partial<{
  staleNotesDays: number;
  noInPersonDays: number;
  stagnationDays: number;
  closeDatePushWindowDays: number;
  criticalActionsLookaheadDays: number;
  pipelineActivityWindowDays: number;
  deepDiveMinAcv: number;
  maxCriticalActionsRows: number;
  maxDealsNeedingAttention: number;
  deepDiveReadinessMaxRows: number;
  dddStalledThresholdDays: number;
  dataWindowYears: number;
  focusAccountSfIds: string[];
}>;

/** Result of POST /wbr-section/narrate — one section's generated prose. */
export interface WbrSectionNarrateResult {
  section: WbrSection;
  recipientSfId: string;
  paragraph: string;
  /** true when the LLM produced valid prose; false when the deterministic
   *  fallback ran (AI error/unavailable). */
  generated: boolean;
  fallbackReason: string | null;
  usage: { inputTokens: number; outputTokens: number } | null;
  model: string | null;
}

/**
 * Cache status indicates whether the connector's cached data is fresh or stale.
 * Why: Helps users understand if they're seeing latest data or should trigger a fresh sync.
 */
export interface CacheStatus {
  status: 'fresh' | 'stale' | 'empty';
  lastRefreshAt?: string;
  expiresAt?: string;
}

/**
 * Test connector response from POST /api/admin/connectors/:id/test
 */
export interface TestConnectorResponse {
  success: boolean;
  message: string;
  latency?: number;
}

/**
 * Sync connector response from POST /api/admin/connectors/:id/sync
 */
export interface SyncConnectorResponse {
  syncId: string;
  status: string;
  startedAt: string;
}

/**
 * Parameters for querying sync jobs.
 * Why: Allows filtering and pagination when fetching sync history.
 */
export interface SyncJobsQueryParams {
  connectorId?: string;
  status?: 'success' | 'failed' | 'running' | 'cancelled';
  limit?: number;
  offset?: number;
}


export interface Settings {
  key: string;
  value: string;
  description?: string;
  updatedAt: string;
}

export interface FeatureFlag {
  key: string;
  enabled: boolean;
  description?: string;
  updatedAt: string;
}

export interface AuditLogEntry {
  id: string;
  action: string;
  resource: string;
  resourceId: string;
  actor: string;
  changes?: Record<string, unknown>;
  createdAt: string;
}

export interface HealthStatus {
  status: 'healthy' | 'degraded' | 'unhealthy';
  uptime: number;
  cpuUsage: number;
  memoryUsage: number;
  databaseConnected: boolean;
  cacheConnected: boolean;
  lastCheck: string;
}

// `CurrentUser` is defined in `client/src/types/` and re-exported at the top of
// this module.

/** A single merged setting from GET /api/settings */
export interface EffectiveSetting {
  key: string;
  value: unknown;
  source: 'default' | 'user_override';
  category: string;
  scope: string;
  isOverridable: boolean;
  description: string | null;
}

/** A saved query from GET /api/settings/saved-queries */
export interface SavedQuery {
  id: string;
  userId: string;
  name: string;
  description: string | null;
  query: Record<string, unknown>;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Input for creating a saved query */
export interface CreateSavedQueryInput {
  name: string;
  description?: string;
  query?: Record<string, unknown>;
  isDefault?: boolean;
}

// ── API Client Class ──
export interface AdminAiTurnsParams {
  days?: number;
  rating?: 'any' | 'up' | 'down' | 'none';
  surface?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface AdminAiTurnRow {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  surface: string;
  feature: string;
  status: string;
  stopReason: string | null;
  /** null when the turn is another user's: Personal Space is private to its owner, so only metadata is shown. */
  question: string | null;
  private?: boolean;
  user: { id: string; email: string; displayName: string } | null;
  rating: 1 | -1 | null;
  reason: string | null;
  latencyMs: number | null;
  estimatedCost: number | null;
  model: string | null;
  tools: string[];
  rounds: number;
  inputTokens: number;
  outputTokens: number;
}

export interface AdminAiTurnsResponse {
  data: AdminAiTurnRow[];
  total: number;
  offset: number;
  limit: number;
  summary: { total: number; up: number; down: number; unrated: number };
}

export interface AdminAiTurnDetail extends Omit<Partial<AdminAiTurnRow>, 'estimatedCost'> {
  id: string;
  question: string | null;
  answer: string | null;
  /** True for another user's turn: question, answer, error and tool inputs are withheld. */
  private?: boolean;
  pageContext: unknown;
  /** May arrive as a string from the detail endpoint; display with Number(). */
  estimatedCost: number | string | null;
  toolCalls: Array<{
    id: string; name: string; input?: unknown; ok: boolean; rowCount: number | null;
    resultBytes: number | null; durationMs: number | null; error: string | null; startedAt: string;
  }>;
  feedback: Array<{
    rating: 1 | -1; reason: string | null; note: string | null; reviewerKind: string;
    reviewer: { email: string; displayName: string } | null; createdAt: string;
  }>;
}

/** Drop unset filters so an action's Zod schema sees "not set", exactly like the old query-string builders. */
function actionInput(params?: object): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params ?? {})) if (v !== undefined && v !== null && v !== '') out[k] = v;
  return out;
}

/** Readable text for a failed api call: the server's message, else its error code, else `fallback`. */
export function apiErrorText(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; error?: unknown };
    if (typeof e.message === 'string' && e.message) return e.message;
    if (typeof e.error === 'string' && e.error) return e.error;
  }
  return fallback;
}

/**
 * ApiClient provides a unified interface to the backend API.
 * Supports multiple base URLs for different API sections:
 * - /api/admin (default) for admin operations
 * - /api/settings for user settings and saved queries
 * - /api/explore for data exploration
 */
/** GET/PUT /api/admin/benchmark/settings. */
export interface BenchmarkSettingsPayload {
  env: 'local' | 'dev' | 'prod';
  config: BenchmarkConfig;
  personas: BenchmarkPersonaRef[];
  models: Array<{ id: string; displayName: string; selected: boolean }>;
  judges: { faithfulness: string; presentation: string };
  users: Array<{ id: string; email: string; displayName: string | null }>;
}

class ApiClient {
  // Default base URL for most admin operations
  private baseUrl: string = '/api/admin';

  // Session-level in-memory cache for GET requests.
  // Prevents redundant network fetches when navigating between pages.
  private cache = new Map<string, { data: any; expiresAt: number }>();
  private static readonly CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

  /**
   * Primary request method with optional baseUrl override.
   * CO-57: Refactored to support different base URLs for various API sections.
   *
   * @param endpoint - The endpoint path (e.g., '/connectors', '/users')
   * @param baseUrl - Optional base URL override (defaults to /api/admin)
   * @param options - Fetch options (method, headers, body, etc.)
   * @returns Parsed JSON response
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    baseUrl?: string
  ): Promise<T> {
    // Use provided baseUrl or fall back to default
    const url = `${baseUrl || this.baseUrl}${endpoint}`;
    const method = (options.method || 'GET').toUpperCase();

    // Return cached data for GET requests if still fresh
    if (method === 'GET') {
      const cached = this.cache.get(url);
      if (cached && Date.now() < cached.expiresAt) {
        return cached.data as T;
      }
    }

    // Invalidate related cache entries on mutations
    if (method !== 'GET') {
      const basePath = (url.split('?')[0] ?? url).replace(/\/[^/]*$/, '');
      for (const key of this.cache.keys()) {
        if (key === url || (key.split('?')[0] ?? key).startsWith(basePath)) {
          this.cache.delete(key);
        }
      }
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> | undefined),
    };

    // Phase G4 (2026-05-19): X-Dev-As header injection retired. The
    // dev identity dropdown is gone from the header (Phase G3); admins
    // preview-as users via /admin/rbac/simulator's Weekly Briefing
    // Preview tab. The server-side `?devAs=` query and `DEV_AS` env
    // paths remain available for ad-hoc curl + E2E orchestration.

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle authentication errors
    if (response.status === 401) {
      // Only redirect for API calls that fail mid-session (e.g., expired session).
      // Don't redirect for /auth/me (useAuth handles that) or if already on /login.
      const isAuthCheck = url.endsWith('/auth/me');
      const isOnLoginPage = window.location.pathname === '/login';
      if (loginRedirectHolds > 0) {
        // A long recording (Ambient mode) is running: navigating away would cut the microphone. Say so instead; the audio keeps buffering on the device.
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      } else if (!isAuthCheck && !isOnLoginPage) {
        window.location.href = '/login';
      }
      throw new Error('Unauthorized');
    }

    // Handle other error responses
    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        error: `HTTP ${response.status}`,
      }));
      throw error;
    }

    // Handle 204 No Content responses
    if (response.status === 204) {
      return undefined as T;
    }

    let result: T;
    try {
      result = await response.json();
    } catch {
      throw new Error('Server returned an unexpected response. The server may be down or restarting — please try again.');
    }

    // Cache successful GET responses
    if (method === 'GET') {
      this.cache.set(url, { data: result, expiresAt: Date.now() + ApiClient.CACHE_TTL_MS });
    }

    return result;
  }

  /** Clear all cached GET responses (e.g., after logout or bulk operations). */
  clearCache(): void {
    this.cache.clear();
  }

  /** Clear cached GET responses matching a URL prefix. */
  clearCachePrefix(prefix: string): void {
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) this.cache.delete(key);
    }
  }

  /**
   * Helper method to make requests with a specific base URL.
   * CO-57: Centralizes the logic for different API base URL sections.
   *
   * @param endpoint - The endpoint path
   * @param baseUrl - The base URL to use (e.g., '/api/settings', '/api/explore')
   * @param options - Fetch options
   * @returns Parsed JSON response
   */
  private async requestWithBase<T>(
    endpoint: string,
    baseUrl: string,
    options: RequestInit = {}
  ): Promise<T> {
    return this.request<T>(endpoint, options, baseUrl);
  }

  /**
   * Call a registry action (server: src/actions). GET by default, input as
   * query string; POST sends JSON. Resolves to the action's result (`data`).
   * `fresh` skips the 5-minute GET cache for this exact URL (polls, and lists a
   * write just changed). Errors follow the same ApiError shape as every other endpoint.
   * `signal` aborts the fetch (page unmounts).
   */
  async callAction<T = unknown>(
    name: string,
    input: Record<string, unknown> = {},
    opts: { method?: 'GET' | 'POST'; fresh?: boolean; signal?: AbortSignal } = {},
  ): Promise<T> {
    const method = opts.method ?? 'GET';
    const signal = opts.signal ? { signal: opts.signal } : {};
    if (method === 'GET') {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(input)) if (v !== undefined && v !== null) qs.set(k, String(v));
      const q = qs.toString();
      const path = `/${name}${q ? `?${q}` : ''}`;
      if (opts.fresh) this.cache.delete(`/api/actions${path}`);
      const res = await this.requestWithBase<{ data: T }>(path, '/api/actions', { ...signal });
      return res.data;
    }
    const res = await this.requestWithBase<{ data: T }>(`/${name}`, '/api/actions', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input), ...signal,
    });
    return res.data;
  }

  // Connectors
  async getConnectors(): Promise<Connector[]> {
    return this.request<Connector[]>('/connectors');
  }

  async getConnector(id: string): Promise<Connector> {
    return this.request<Connector>(`/connectors/${id}`);
  }

  /**
   * Get connector detail including server-enriched schedule fields
   * (scheduleMode + nextRuns) and the most recent 10 sync_metadata rows.
   * Bypasses the cached `getConnector` so the UI gets fresh state after
   * a save / Sync Now.
   */
  async getConnectorDetail(id: string): Promise<Connector> {
    const res = await this.request<{ data: Connector }>(`/connectors/${id}`);
    return res.data;
  }

  /**
   * Update connector configuration and/or schedule fields. The server
   * reschedules the Bull repeatable in-process when scheduling fields
   * change on an enabled connector.
   */
  async patchConnector(id: string, data: Partial<Connector>): Promise<Connector> {
    const res = await this.request<{ data: Connector }>(`/connectors/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return res.data;
  }

  /**
   * List per-(connector, object_type) watermark rows.
   * Used by the Schedule & State panel on the Connector Detail page.
   */
  async getConnectorWatermarks(id: string): Promise<ConnectorWatermark[]> {
    const res = await this.request<{ data: ConnectorWatermark[] }>(`/connectors/${id}/watermarks`);
    return res.data;
  }

  /**
   * Reset a single watermark. The next sync treats that object as if
   * it had never run, pulling everything. Pass objectType='*' to reset
   * every watermark for the connector.
   */
  async resetConnectorWatermark(id: string, objectType: string): Promise<{ deletedCount: number }> {
    const res = await this.request<{ data: { deletedCount: number } }>(
      `/connectors/${id}/watermarks/${encodeURIComponent(objectType)}`,
      { method: 'DELETE' },
    );
    return res.data;
  }

  async createConnector(data: Omit<Connector, 'id' | 'createdAt' | 'updatedAt'>): Promise<Connector> {
    return this.request<Connector>('/connectors', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateConnector(id: string, data: Partial<Connector>): Promise<Connector> {
    return this.request<Connector>(`/connectors/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async toggleConnector(id: string): Promise<Connector> {
    return this.request<Connector>(`/connectors/${id}/toggle`, {
      method: 'PATCH',
    });
  }

  async deleteConnector(id: string): Promise<void> {
    return this.request<void>(`/connectors/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Test the connection to a connector endpoint.
   * Why: Validates connector configuration and connectivity before syncing.
   *
   * @param id - Connector ID to test
   * @returns Test result with success flag, message, and optional latency
   */
  async testConnector(id: string): Promise<TestConnectorResponse> {
    return this.request<TestConnectorResponse>(`/connectors/${id}/test`, {
      method: 'POST',
    });
  }

  /**
   * Start a synchronization job for a connector.
   * Why: Initiates data sync; returns job ID so caller can poll for progress.
   *
   * @param id - Connector ID to sync
   * @param options - Optional sync parameters (e.g., incremental, force full)
   * @returns Sync job info with ID and initial status
   */
  async syncConnector(id: string, options?: Record<string, unknown>): Promise<SyncConnectorResponse> {
    return this.request<SyncConnectorResponse>(`/connectors/${id}/sync`, {
      method: 'POST',
      body: options ? JSON.stringify(options) : undefined,
    });
  }

  /**
   * Get recent sync jobs with optional filtering and pagination.
   * CO-45: Sync queue monitoring endpoint.
   * Supports filtering by status, connector, and date range.
   *
   * @param params - Query parameters (status, connectorId, page, limit, etc.)
   * @returns Paginated list of sync jobs with connector info
   */
  async getSyncJobs(params?: Record<string, unknown>): Promise<{ data: any[]; pagination: { total: number; page: number; limit: number; pages: number } }> {
    const queryString = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          queryString.append(key, String(value));
        }
      });
    }

    const query = queryString.toString() ? `?${queryString.toString()}` : '';
    return this.request(`/sync-jobs${query}`);
  }

  /**
   * Get details of a specific sync job.
   * Why: Allows polling for sync progress or checking historical job details.
   *
   * @param id - Sync job ID
   * @returns Detailed sync job information
   */
  async getSyncJobById(id: string): Promise<SyncJob> {
    return this.request<SyncJob>(`/sync-jobs/${id}`);
  }

  /**
   * Get aggregate statistics about sync jobs in the last 24 hours.
   * Why: Provides quick health metrics for the admin dashboard.
   * Returns counts by status, average duration, success rate, and total records synced.
   *
   * @returns Statistics object with job counts and performance metrics
   */
  async getSyncJobStats(): Promise<any> {
    return this.request('/sync-jobs/stats');
  }

  /** Compass App (mobile) — a user's active device sessions, for the Users
   *  tab's Edit dialog. Backed by GET /api/admin/users/:id/devices. */
  async listUserDevices(userId: string): Promise<DeviceSessionListResponse> {
    return this.request(`/users/${encodeURIComponent(userId)}/devices`);
  }

  /** Revoke one of a user's mobile device sessions (signs that phone out on its
   *  next request). DELETE /api/admin/users/:id/devices/:deviceId → 204. */
  async revokeUserDevice(userId: string, deviceId: string): Promise<void> {
    await this.request<void>(
      `/users/${encodeURIComponent(userId)}/devices/${encodeURIComponent(deviceId)}`,
      { method: 'DELETE' },
    );
  }

  // ── Users tab → Connectors: status rows and the user's synced Personal Space
  //    data (docs/Integrations/Connectors/UX_DESIGN.html §5). Counts, sizes and
  //    statuses only — never a token or content. These reads always go to the
  //    server (the GET cache would show a deleted space or a stale chip). ──

  /** Status-only connector rows for a set of users, keyed by user id (users
   *  without a row are absent). GET /api/admin/user-connectors/users?ids=… (audited). */
  async getUserConnectorStatuses(ids: string[]): Promise<Record<string, AdminUserConnector[]>> {
    if (ids.length === 0) return {};
    const endpoint = `/user-connectors/users?ids=${encodeURIComponent(ids.join(','))}`;
    this.clearCachePrefix(`${this.baseUrl}${endpoint}`);
    const body = await this.request<{ users?: Record<string, AdminUserConnector[]> }>(endpoint);
    return body.users ?? {};
  }

  /** One user's Personal Space per source — counts and sizes only.
   *  GET /api/admin/personal-space/users/:id; [] when the user no longer exists. */
  async getPersonalSpaceSummary(userId: string): Promise<PersonalSpaceSourceSummary[]> {
    const endpoint = `/personal-space/users/${encodeURIComponent(userId)}`;
    this.clearCachePrefix(`${this.baseUrl}${endpoint}`);
    try {
      const body = await this.request<{ sources?: PersonalSpaceSourceSummary[] }>(endpoint);
      return body.sources ?? [];
    } catch (err) {
      const e = err as { statusCode?: number; error?: string } | null;
      if (e?.statusCode === 404 || e?.error === 'Not Found' || e?.error === 'HTTP 404') return [];
      throw err;
    }
  }

  /** The admin delete of one source of a user's Personal Space (reason required,
   *  audited, refused under legal hold). Also disconnects that provider for the
   *  user so the sync does not copy the data back. Idempotent: repeating it with
   *  nothing left removes leftover files. DELETE …/users/:id/sources/:source. */
  async deletePersonalSpaceSource(userId: string, source: PersonalSpaceSource, reason: string): Promise<PersonalSpacePurgeResult> {
    type Raw = {
      purged?: { items?: number; disconnected?: boolean; filesError?: string | null; indexError?: string | null };
      disconnected?: boolean;
      filesError?: string | null;
      indexError?: string | null;
      sources?: PersonalSpaceSourceSummary[];
    };
    const body = (await this.request<Raw>(
      `/personal-space/users/${encodeURIComponent(userId)}/sources/${encodeURIComponent(source)}`,
      { method: 'DELETE', body: JSON.stringify({ reason }) },
    )) ?? {};
    return {
      items: body.purged?.items ?? 0,
      disconnected: Boolean(body.disconnected ?? body.purged?.disconnected),
      filesError: body.filesError ?? body.purged?.filesError ?? null,
      indexError: body.indexError ?? body.purged?.indexError ?? null,
      sources: body.sources ?? [],
    };
  }

  /** Search SF users by name or email. Used by the WBR run monitor
   *  recipient picker so admins don't need to remember SF IDs. */
  async searchSfUsers(
    q: string,
  ): Promise<{ data: Array<{ sfId: string; name: string; email: string; title: string | null }> }> {
    return this.request(`/users/sf-search?q=${encodeURIComponent(q)}`);
  }

  /** Sales team eligible for WBR — the configured lead orgs + standalone
   *  execs (anchored by stable email), ordered as a depth-first walk of
   *  the SF manager tree. Each row carries `parentId` (the recipient's SF
   *  manager) so the picker can indent by org depth. Used by the Generate
   *  panel and the WBR Analysis tab. */
  async listSfUsers(): Promise<{
    data: Array<{
      sfId: string;
      name: string;
      title: string | null;
      tier: 'exec' | 'rvp' | 'ae';
      parentId: string | null;
    }>;
  }> {
    // Served by the Customization router (requireWbrAdmin) rather than
    // /api/admin/wbr-runs/recipients (requireAdmin), so weekly-briefing-admins
    // can pick any recipient too.
    return this.request('/wbr/section-customization/recipients');
  }

  // ── WBR Run Monitor (CO-401) ─────────────────────────────────

  /**
   * Paginated list of Weekly Briefing runs. Drives the
   * /admin/wbr-runs list view.
   *
   * @param params Optional `week` / `status` / `recipientSfId` /
   * `triggeredBy` / `limit` / `offset` filters.
   */
  async getWbrRuns(params?: {
    week?: string;
    status?: 'in_progress' | 'completed' | 'partial' | 'failed';
    recipientSfId?: string;
    triggeredBy?: 'cron' | 'page_view' | 'admin_regenerate' | 'simulator';
    limit?: number;
    offset?: number;
  }): Promise<{
    data: WbrRunListRow[];
    total: number;
    offset: number;
    limit: number;
  }> {
    const qs = new URLSearchParams();
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== '') qs.append(k, String(v));
      }
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.request(`/wbr-runs${suffix}`);
  }

  /**
   * Single run + the full step timeline. Drives the
   * /admin/wbr-runs drilldown view.
   */
  async getWbrRunById(id: string): Promise<{ data: WbrRunDetail }> {
    return this.request(`/wbr-runs/${encodeURIComponent(id)}`);
  }

  // ── WBR Email Log ────────────────────────────────────────────

  /**
   * Paginated history of every email the WBR pipeline attempted — sent,
   * skipped and failed. Drives Settings → Weekly Briefings → Email Log.
   */
  async getWbrEmailLog(params?: {
    week?: string;
    kind?: 'briefing' | 'hygiene_report';
    status?: 'ok' | 'skipped' | 'failed';
    recipientSfId?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    data: WbrEmailLogRow[];
    total: number;
    offset: number;
    limit: number;
  }> {
    const qs = new URLSearchParams();
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== '') qs.append(k, String(v));
      }
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.request(`/wbr-runs/email-log${suffix}`);
  }

  /**
   * Subject line for one log row, plus whether its body can be
   * reconstructed (hygiene-report rows cannot — they render from a live
   * score rather than stored run data).
   */
  async getWbrEmailSubject(stepId: string): Promise<{
    kind: 'briefing' | 'hygiene_report';
    subject: string | null;
    previewAvailable: boolean;
    /** A rebuild from today's data is offerable (hygiene rows with no archive). */
    liveRebuildAvailable: boolean;
    unavailableReason: string | null;
  }> {
    return this.request(`/wbr-runs/email-log/${encodeURIComponent(stepId)}/subject`);
  }

  /**
   * URL of the rendered email body. Returned as a URL rather than
   * fetched text because the consumer is an iframe `src` — the endpoint
   * responds with `text/html`, not JSON, so `request()` can't parse it.
   */
  wbrEmailPreviewUrl(stepId: string, mode?: 'live'): string {
    const qs = mode === 'live' ? '?mode=live' : '';
    return `/api/admin/wbr-runs/email-log/${encodeURIComponent(stepId)}/preview${qs}`;
  }

  // ── WBR Section SQL Playground (CO-402) ──────────────────────

  /**
   * Pre-load the production SQL for a section. The playground UI
   * calls this on mount + whenever the admin switches sections so
   * the candidate editor starts from the current production behaviour.
   */
  async getWbrSectionSql(
    section: WbrSection,
  ): Promise<{ data: { section: WbrSection; sql: string } }> {
    return this.request(`/wbr-section/sql?section=${encodeURIComponent(section)}`);
  }

  /**
   * Run production + candidate SQL against a test recipient's scope.
   * The validator + scope-injection happen server-side; the client
   * just submits the candidate text and renders both rowsets.
   */
  async previewWbrSection(input: {
    section: WbrSection;
    candidateSql: string;
    recipientSfId: string;
    reportDate: string;
    /** Customization Step-1 draft thresholds; merged server-side over the saved
     *  config so the bound SQL params reflect the admin's unsaved knobs. */
    queryConfig?: WbrQueryConfigDraft;
  }): Promise<{ data: WbrSectionPreviewResult }> {
    return this.request(`/wbr-section/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  /**
   * Customization Step 3 — regenerate one section's narrative from rows already
   * returned by {@link previewWbrSection} (no SQL re-run). Lets an admin A/B a
   * prompt / model against the same data. Prompt/LLM fields are
   * optional and fall back to the saved prompt config server-side.
   */
  async narrateWbrSection(input: {
    section: WbrSection;
    recipientSfId: string;
    payload: unknown;
    systemPrompt?: string;
    sectionPrompt?: string;
    tierLabels?: Partial<{ ae: string; rvp: string; exec: string }>;
    maxTokens?: number;
    model?: string;
  }): Promise<{ data: WbrSectionNarrateResult }> {
    return this.request(`/wbr-section/narrate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  /**
   * Cancel a queued or active sync job.
   * Why: Allows admins to stop long-running or stuck jobs.
   * Returns 400 if the job is already completed or in a terminal state.
   *
   * @param id - Sync job ID to cancel
   * @returns Updated job with cancelled status
   */
  async cancelSyncJob(id: string): Promise<SyncJob> {
    return this.request<SyncJob>(`/sync-jobs/${id}/cancel`, {
      method: 'POST',
    });
  }

  /**
   * Get cache status for a connector.
   * Why: Shows whether cached data is fresh or needs refresh.
   *
   * @returns Current cache status across all connectors
   */
  async getCacheStatus(): Promise<CacheStatus> {
    return this.request<CacheStatus>('/cache/status');
  }

  // Sync History
  async getSyncHistory(limit?: number, offset?: number): Promise<SyncHistory[]> {
    const params = new URLSearchParams();
    if (limit !== undefined) params.append('limit', limit.toString());
    if (offset !== undefined) params.append('offset', offset.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<SyncHistory[]>(`/sync-history${query}`);
  }

  async getSyncHistoryEntry(id: string): Promise<SyncHistory> {
    return this.request<SyncHistory>(`/sync-history/${id}`);
  }

  // Settings
  async getSettings(): Promise<Settings[]> {
    return this.request<Settings[]>('/settings');
  }

  async updateSetting(key: string, value: string): Promise<Settings> {
    return this.request<Settings>(`/settings/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  }

  // Feature Flags
  async getFeatureFlags(): Promise<FeatureFlag[]> {
    return this.request<FeatureFlag[]>('/feature-flags');
  }

  async updateFeatureFlag(key: string, enabled: boolean): Promise<FeatureFlag> {
    return this.request<FeatureFlag>(`/feature-flags/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    });
  }

  // AI conversations ledger (Admin → Settings → AI Conversations).
  // Always fresh: the cache entry is cleared first so ratings show live.
  // ── CompassAI Benchmark (docs/AiQuality/Benchmark) — /api/admin/benchmark ──
  // Live data: every GET clears its cache entry first, like the AI Conversations tab.

  async getBenchmarkOverview(): Promise<BenchmarkOverview> {
    this.clearCachePrefix('/api/admin/benchmark/overview');
    return this.request<BenchmarkOverview>('/benchmark/overview');
  }

  async listBenchmarkRuns(params: { kind?: string; limit?: number } = {}): Promise<{ data: BenchmarkRunRow[] }> {
    const qs = new URLSearchParams();
    if (params.kind) qs.set('kind', params.kind);
    if (params.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    this.clearCachePrefix('/api/admin/benchmark/runs');
    return this.request<{ data: BenchmarkRunRow[] }>(`/benchmark/runs${suffix}`);
  }

  async getBenchmarkRun(id: string): Promise<BenchmarkRunDetail> {
    this.clearCachePrefix(`/api/admin/benchmark/runs/${id}`);
    return this.request<BenchmarkRunDetail>(`/benchmark/runs/${encodeURIComponent(id)}`);
  }

  async estimateBenchmarkRun(body: BenchmarkRunRequest): Promise<BenchmarkEstimate & { planned: number; kind: string; personas: BenchmarkPersonaRef[] }> {
    return this.request(`/benchmark/estimate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  }

  async startBenchmarkRun(body: BenchmarkRunRequest): Promise<BenchmarkRunDetail> {
    return this.request<BenchmarkRunDetail>(`/benchmark/runs`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  }

  async cancelBenchmarkRun(id: string): Promise<BenchmarkRunDetail> {
    return this.request<BenchmarkRunDetail>(`/benchmark/runs/${encodeURIComponent(id)}/cancel`, { method: 'POST' });
  }

  async resumeBenchmarkRun(id: string): Promise<BenchmarkRunDetail> {
    return this.request<BenchmarkRunDetail>(`/benchmark/runs/${encodeURIComponent(id)}/resume`, { method: 'POST' });
  }

  async patchBenchmarkRun(id: string, body: { note?: string | null; label?: string | null; baseline?: boolean }): Promise<BenchmarkRunDetail> {
    return this.request<BenchmarkRunDetail>(`/benchmark/runs/${encodeURIComponent(id)}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  }

  async listBenchmarkResults(runId: string, params: Record<string, string | number | undefined> = {}): Promise<{ data: BenchmarkResultRow[]; total: number }> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') qs.set(k, String(v));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    this.clearCachePrefix(`/api/admin/benchmark/runs/${runId}/results`);
    return this.request(`/benchmark/runs/${encodeURIComponent(runId)}/results${suffix}`);
  }

  async getBenchmarkResult(id: string): Promise<BenchmarkResultDetail & { extra?: { completeness?: unknown; scope?: string[] | null; routing?: string[] | null; meta?: Record<string, unknown> | null } | null }> {
    this.clearCachePrefix(`/api/admin/benchmark/results/${id}`);
    return this.request(`/benchmark/results/${encodeURIComponent(id)}`);
  }

  async compareBenchmarkRuns(a: string, b: string): Promise<BenchmarkCompareResponse> {
    this.clearCachePrefix('/api/admin/benchmark/compare');
    return this.request<BenchmarkCompareResponse>(`/benchmark/compare?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`);
  }

  async listBenchmarkCases(suite?: BenchmarkSuite): Promise<{ data: BenchmarkCaseRow[]; caseSetVersion: string }> {
    this.clearCachePrefix('/api/admin/benchmark/cases');
    return this.request(`/benchmark/cases${suite ? `?suite=${encodeURIComponent(suite)}` : ''}`);
  }

  async saveBenchmarkCase(spec: BenchmarkCaseSpecInput, existingId?: string): Promise<{ id: string; caseSetVersion: string }> {
    return this.request(existingId ? `/benchmark/cases/${encodeURIComponent(existingId)}` : `/benchmark/cases`, { method: existingId ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(spec) });
  }

  async disableBenchmarkCase(id: string): Promise<{ id: string; enabled: boolean }> {
    return this.request(`/benchmark/cases/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  async tryBenchmarkCase(id: string, body: { persona: string; surface: string; modelId?: string }): Promise<{ runId: string }> {
    return this.request(`/benchmark/cases/${encodeURIComponent(id)}/try`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  }

  async exportBenchmarkCases(): Promise<Record<string, unknown>> {
    this.clearCachePrefix('/api/admin/benchmark/cases/export');
    return this.request(`/benchmark/cases/export`);
  }

  async getBenchmarkImportCandidates(days = 30): Promise<{ data: ImportCandidate[] }> {
    this.clearCachePrefix('/api/admin/benchmark/cases/import-candidates');
    return this.request(`/benchmark/cases/import-candidates?days=${days}`);
  }

  async getBenchmarkSettings(): Promise<BenchmarkSettingsPayload> {
    this.clearCachePrefix('/api/admin/benchmark/settings');
    return this.request<BenchmarkSettingsPayload>(`/benchmark/settings`);
  }

  async saveBenchmarkSettings(patch: Partial<BenchmarkConfig> | Record<string, unknown>): Promise<BenchmarkSettingsPayload> {
    return this.request<BenchmarkSettingsPayload>(`/benchmark/settings`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) });
  }

  async getBenchmarkFixtures(): Promise<BenchmarkFixturesStatus> {
    this.clearCachePrefix('/api/admin/benchmark/fixtures');
    return this.request<BenchmarkFixturesStatus>(`/benchmark/fixtures`);
  }

  async seedBenchmarkFixtures(): Promise<BenchmarkFixturesStatus> {
    return this.request<BenchmarkFixturesStatus>(`/benchmark/fixtures/seed`, { method: 'POST' });
  }

  async removeBenchmarkFixtures(): Promise<BenchmarkFixturesStatus> {
    return this.request<BenchmarkFixturesStatus>(`/benchmark/fixtures`, { method: 'DELETE' });
  }

  async getBenchmarkUsageWeights(): Promise<UsageWeightsResponse> {
    this.clearCachePrefix('/api/admin/benchmark/usage-weights');
    return this.request<UsageWeightsResponse>(`/benchmark/usage-weights`);
  }

  async recomputeBenchmarkUsageWeights(): Promise<UsageWeightsResponse> {
    return this.request<UsageWeightsResponse>(`/benchmark/usage-weights/recompute`, { method: 'POST' });
  }

  async getAdminAiTurns(params: AdminAiTurnsParams = {}): Promise<AdminAiTurnsResponse> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') qs.set(k, String(v));
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    this.clearCachePrefix('/api/admin/ai/turns');
    return this.requestWithBase<AdminAiTurnsResponse>(`/ai/turns${suffix}`, '/api/admin');
  }

  async getAdminAiTurn(id: string): Promise<{ data: AdminAiTurnDetail }> {
    this.clearCachePrefix(`/api/admin/ai/turns/${encodeURIComponent(id)}`);
    return this.requestWithBase<{ data: AdminAiTurnDetail }>(`/ai/turns/${encodeURIComponent(id)}`, '/api/admin');
  }

  // AI model (Admin → Settings → AI model)
  async getAIModelSettings(): Promise<AIModelSettings> {
    return this.request<AIModelSettings>('/ai/model');
  }

  async updateAIModel(modelId: string): Promise<AIModelSettings> {
    return this.request<AIModelSettings>('/ai/model', {
      method: 'PUT',
      body: JSON.stringify({ modelId }),
    });
  }

  // Compass Slack bot (CO-460)
  async getSlackBotSettings(): Promise<{ settings: SlackBotSettings; defaults: SlackBotSettings }> {
    return this.request<{ settings: SlackBotSettings; defaults: SlackBotSettings }>('/slack/settings');
  }

  async updateSlackBotSettings(settings: SlackBotSettings): Promise<{ settings: SlackBotSettings }> {
    return this.request<{ settings: SlackBotSettings }>('/slack/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async listSlackTranscripts(
    params: SlackTranscriptQuery = {},
  ): Promise<{ data: SlackTranscriptRow[]; pagination: { total: number; limit: number; offset: number; hasMore: boolean } }> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') qs.append(k, String(v));
    }
    const query = qs.toString() ? `?${qs.toString()}` : '';
    return this.request<{ data: SlackTranscriptRow[]; pagination: { total: number; limit: number; offset: number; hasMore: boolean } }>(
      `/slack/transcripts${query}`,
    );
  }

  async simulateSlackBot(input: { text: string; channelType: 'im' | 'channel'; asUserEmail?: string; sessionId: string; grade?: boolean }): Promise<SlackSimulateResult> {
    return this.request<SlackSimulateResult>('/slack/simulate', { method: 'POST', body: JSON.stringify(input) });
  }

  /** Compass users selectable in the bot simulator's "preview as" dropdown
   *  (reuses the RBAC simulator's user list). */
  async listSimulatableUsers(): Promise<{ data: Array<{ id: string; displayName: string; email: string }> }> {
    return this.request<{ data: Array<{ id: string; displayName: string; email: string }> }>('/rbac/simulate/users');
  }

  // ── Assistant Simulators (docs/AiQuality Phase F) ── run an assistant as a chosen user, optionally grade it.
  async assistantCompassAiSimulate(input: { asUserId: string; question: string; grade?: boolean }): Promise<CompassAiSimResult> {
    return this.request<CompassAiSimResult>('/assistant/compass-ai/simulate', { method: 'POST', body: JSON.stringify(input) });
  }

  async assistantCompare(input: {
    asUserId: string;
    question: string;
    channelType?: 'im' | 'channel';
    sessionId?: string;
    webHistory?: Array<{ role: 'user' | 'assistant'; content: string }>;
    slackHistory?: Array<{ role: 'user' | 'assistant'; content: string }>;
  }): Promise<AssistantCompareResult> {
    return this.request<AssistantCompareResult>('/assistant/compare', { method: 'POST', body: JSON.stringify(input) });
  }

  // Audit Log
  async getAuditLog(limit?: number, offset?: number): Promise<AuditLogEntry[]> {
    const params = new URLSearchParams();
    if (limit !== undefined) params.append('limit', limit.toString());
    if (offset !== undefined) params.append('offset', offset.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<AuditLogEntry[]>(`/audit-log${query}`);
  }

  // Health
  async getHealth(): Promise<HealthStatus> {
    return this.request<HealthStatus>('/health');
  }

  // Auth (routes are at /auth/*, not /api/admin/auth/*)
  async getCurrentUser(): Promise<CurrentUser> {
    return this.requestWithBase<CurrentUser>('/me', '/auth');
  }

  async logout(): Promise<void> {
    return this.requestWithBase<void>('/logout', '/auth', {
      method: 'POST',
    });
  }

  /**
   * CO-113: Begin impersonation. Admin-only on the backend; the
   * server validates and returns the new effective identity.
   */
  async startImpersonation(userId: string): Promise<{
    impersonating: { id: string; email: string; displayName: string; role: string };
    realUser: { id: string; email: string };
  }> {
    return this.request(`/impersonate/${encodeURIComponent(userId)}`, { method: 'POST' });
  }

  /**
   * CO-113: End impersonation. Hits the /auth-mounted exit endpoint
   * because /api/admin/* would 403 the impersonated (non-admin) user.
   */
  async stopImpersonation(): Promise<{ ok: boolean }> {
    return this.requestWithBase<{ ok: boolean }>('/impersonate/exit', '/auth', {
      method: 'POST',
    });
  }

  // ── User Settings (non-admin, /api/settings) ──
  // CO-57: Refactored to use this.request() with base URL override

  /**
   * GET /api/settings — Merged settings (admin defaults + user overrides).
   * Each setting includes a `source` field: "default" or "user_override".
   * Uses the /api/settings base URL instead of the default /api/admin.
   */
  async getUserSettings(): Promise<EffectiveSetting[]> {
    // Wrapper response includes data array
    const res = await this.requestWithBase<{ data: EffectiveSetting[] }>(
      '',
      '/api/settings'
    );
    return res.data;
  }

  /**
   * PUT /api/settings/:key — Set a user override for a preference setting.
   * Uses the /api/settings base URL instead of the default /api/admin.
   *
   * @param key - The setting key to override
   * @param value - The new value for the setting
   * @returns The updated effective setting
   */
  async setUserSetting(key: string, value: unknown): Promise<EffectiveSetting> {
    const res = await this.requestWithBase<{ data: EffectiveSetting }>(
      `/${encodeURIComponent(key)}`,
      '/api/settings',
      {
        method: 'PUT',
        body: JSON.stringify({ value }),
      }
    );
    return res.data;
  }

  /**
   * DELETE /api/settings/:key — Remove user override (revert to admin default).
   * Uses the /api/settings base URL instead of the default /api/admin.
   *
   * @param key - The setting key to remove
   */
  async removeUserSetting(key: string): Promise<void> {
    return this.requestWithBase<void>(
      `/${encodeURIComponent(key)}`,
      '/api/settings',
      {
        method: 'DELETE',
      }
    );
  }

  // ── Saved Queries (/api/settings/saved-queries) ──
  // CO-57: Refactored to use this.request() with base URL override

  /**
   * GET /api/settings/saved-queries — Retrieve all saved queries for the current user.
   * Uses the /api/settings base URL.
   *
   * @returns Array of saved queries
   */
  async getSavedQueries(): Promise<SavedQuery[]> {
    // Wrapper response includes data array
    const res = await this.requestWithBase<{ data: SavedQuery[] }>(
      '/saved-queries',
      '/api/settings'
    );
    return res.data;
  }

  /**
   * POST /api/settings/saved-queries — Create a new saved query.
   * Uses the /api/settings base URL.
   *
   * @param data - The saved query input (name, description, query, etc.)
   * @returns The created saved query with ID and timestamps
   */
  async createSavedQuery(data: CreateSavedQueryInput): Promise<SavedQuery> {
    const res = await this.requestWithBase<{ data: SavedQuery }>(
      '/saved-queries',
      '/api/settings',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return res.data;
  }

  /**
   * PUT /api/settings/saved-queries/:id — Update an existing saved query.
   * Uses the /api/settings base URL.
   *
   * @param id - The saved query ID
   * @param data - Partial update data
   * @returns The updated saved query
   */
  async updateSavedQuery(id: string, data: Partial<CreateSavedQueryInput>): Promise<SavedQuery> {
    const res = await this.requestWithBase<{ data: SavedQuery }>(
      `/saved-queries/${id}`,
      '/api/settings',
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
    return res.data;
  }

  /**
   * DELETE /api/settings/saved-queries/:id — Delete a saved query.
   * Uses the /api/settings base URL.
   *
   * @param id - The saved query ID to delete
   */
  async deleteSavedQuery(id: string): Promise<void> {
    return this.requestWithBase<void>(
      `/saved-queries/${id}`,
      '/api/settings',
      {
        method: 'DELETE',
      }
    );
  }

  // ── Explore reads — registry actions (src/actions/explore/), field-redacted and cached per viewer ──

  async getAccountsData(params?: Record<string, any>, signal?: AbortSignal): Promise<DataResponse<any>> {
    return this.callAction<DataResponse<any>>('explore_accounts', actionInput(params), { signal });
  }

  async getContractsData(params?: Record<string, any>, signal?: AbortSignal): Promise<DataResponse<any>> {
    return this.callAction<DataResponse<any>>('explore_contracts', actionInput(params), { signal });
  }

  async getImplementationsData(params?: Record<string, any>, signal?: AbortSignal): Promise<DataResponse<any>> {
    return this.callAction<DataResponse<any>>('explore_implementations', actionInput(params), { signal });
  }

  async getTrainingData(params?: Record<string, any>, signal?: AbortSignal): Promise<DataResponse<any>> {
    return this.callAction<DataResponse<any>>('explore_training', actionInput(params), { signal });
  }

  async getContactsData(params?: Record<string, any>): Promise<DataResponse<any>> {
    return this.callAction<DataResponse<any>>('explore_contacts', actionInput(params));
  }

  async getOpportunitiesData(params?: Record<string, any>, signal?: AbortSignal): Promise<DataResponse<any>> {
    return this.callAction<DataResponse<any>>('explore_opportunities', actionInput(params), { signal });
  }

  async getSitesData(params?: Record<string, any>, signal?: AbortSignal): Promise<DataResponse<any>> {
    return this.callAction<DataResponse<any>>('explore_sites', actionInput(params), { signal });
  }

  async getBeaconsData(params?: Record<string, any>, signal?: AbortSignal): Promise<DataResponse<any>> {
    return this.callAction<DataResponse<any>>('explore_beacons', actionInput(params), { signal });
  }

  async getAmplitudeDau(params?: { days?: number }, signal?: AbortSignal): Promise<{ data: any[]; days: number }> {
    return this.callAction<{ data: any[]; days: number }>('explore_amplitude_dau', actionInput({ days: params?.days || undefined }), { signal });
  }

  async getAmplitudeEvents(params?: { limit?: number; offset?: number }): Promise<DataResponse<any>> {
    return this.callAction<DataResponse<any>>('explore_amplitude_events', actionInput({ limit: params?.limit || undefined, offset: params?.offset || undefined }));
  }

  async getAmplitudeMau(params?: { months?: number }, signal?: AbortSignal): Promise<any> {
    return this.callAction<any>('explore_amplitude_mau', actionInput({ months: params?.months || undefined }), { signal });
  }

  // ── Cache Management ──
  // CO-57: New cache invalidation and refresh methods

  /**
   * POST /api/admin/cache/invalidate — Invalidate cache for a specific connector.
   * Marks the cache as stale so data will be re-fetched on next access.
   *
   * @param connectorId - The connector ID whose cache should be invalidated
   * @returns Confirmation of cache invalidation
   */
  async invalidateCache(connectorId: string): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(
      `/cache/invalidate/${connectorId}`,
      {
        method: 'POST',
      }
    );
  }

  /**
   * POST /api/admin/cache/refresh — Refresh cache for a specific connector.
   * Triggers an immediate cache refresh to pull latest data.
   *
   * @param connectorId - The connector ID whose cache should be refreshed
   * @returns Confirmation of cache refresh request
   */
  async refreshCache(connectorId: string): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(
      `/cache/refresh/${connectorId}`,
      {
        method: 'POST',
      }
    );
  }

  /**
   * POST /api/admin/cache/purge — Completely purge cache for a specific connector.
   * Deletes all cached data so it will be fully re-fetched on next access.
   *
   * @param connectorId - The connector ID whose cache should be purged
   * @returns Confirmation of cache purge
   */
  async purgeCache(connectorId: string): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(
      `/cache/purge/${connectorId}`,
      {
        method: 'POST',
      }
    );
  }

  // ── API Cache Management (/api/admin/api-cache) ──

  async getCacheRules(): Promise<{ data: any[]; total: number }> {
    return this.request('/api-cache/rules');
  }

  async updateCacheRule(id: string, data: { enabled?: boolean; ttlSeconds?: number; description?: string }): Promise<{ data: any }> {
    return this.request(`/api-cache/rules/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async getCacheEntries(endpoint?: string): Promise<{ data: any[]; total: number }> {
    const q = endpoint ? `?endpoint=${encodeURIComponent(endpoint)}` : '';
    return this.request(`/api-cache/entries${q}`);
  }

  async getCacheStats(): Promise<{ data: any }> {
    return this.request('/api-cache/stats');
  }

  // ── Server resources (System Health → Server) ──────────────
  // Three calls, not one: the snapshot is cheap and refreshed often, the
  // disk `du` behind it is cached server-side, and the warehouse storage
  // query can fail on its own without blanking the rest of the tab.

  /** Host memory/CPU, filesystem capacity, and the on-disk breakdown. */
  async getServerStats(refresh = false): Promise<{ data: ServerStats }> {
    return this.request(`/system/stats${refresh ? '?refresh=1' : ''}`);
  }

  /** Sampled resource history for the last `hours` (capped at the 30d retention). */
  async getServerMetricHistory(hours: number): Promise<{ data: ServerMetricHistory }> {
    return this.request(`/system/history?hours=${encodeURIComponent(String(hours))}`);
  }

  /** Per-table bytes in ClickHouse + Postgres — what the stored data costs. */
  async getServerStorage(refresh = false): Promise<{ data: ServerStorage }> {
    return this.request(`/system/storage${refresh ? '?refresh=1' : ''}`);
  }

  async nukeCacheEntries(): Promise<{ data: { deleted: number } }> {
    return this.request('/api-cache/entries', { method: 'DELETE' });
  }

  async deleteCacheEntry(id: string): Promise<{ data: { deleted: number } }> {
    return this.request(`/api-cache/entries/${id}`, { method: 'DELETE' });
  }

  async deleteCacheEntriesByEndpoint(endpoint: string): Promise<{ data: { endpoint: string; deleted: number } }> {
    return this.request(`/api-cache/entries/by-endpoint${endpoint}`, { method: 'DELETE' });
  }

  // ── Accounts + Clinical reads (registry actions, /api/actions/<name>) ──

  /** list_accounts — RBAC-scoped account list with rollups + filters (GET /api/actions/list_accounts).
   *  Named `listAccounts` (not `getAccounts`) to avoid colliding with the
   *  removed legacy `getAccounts` client method. */
  async listAccounts(params?: AccountsListParams): Promise<AccountsListResponse> {
    return this.callAction<AccountsListResponse>('list_accounts', actionInput(params));
  }

  /** account_detail — throws an ApiError with statusCode 404 (unknown) / 403 (out of scope). */
  async getAccount(sfId: string): Promise<AccountDetail> {
    return this.callAction<AccountDetail>('account_detail', { sfId });
  }

  /** account_enrichment — Opportunity Insights (account-admin). */
  async getAccountEnrichment(sfId: string): Promise<AccountEnrichment> {
    return this.callAction<AccountEnrichment>('account_enrichment', { sfId });
  }

  /** list_parent_accounts — paginated list of accounts with children via SF ParentId. */
  async listParentAccounts(params: { limit?: number; offset?: number; search?: string; region?: string; ae?: string; csc?: string } = {}): Promise<{ data: ParentAccountRow[]; pagination: { total: number; offset: number; limit: number }; facets: ParentAccountFacets }> {
    return this.callAction<{ data: ParentAccountRow[]; pagination: { total: number; offset: number; limit: number }; facets: ParentAccountFacets }>(
      'list_parent_accounts', actionInput(params),
    );
  }

  /** cs_kpis — the CS KPI tiles (404 feature_disabled when the cs_kpis flag is off). */
  async getCsKpis(params: CsKpisParams = {}, signal?: AbortSignal): Promise<CsKpisResponse> {
    return this.callAction<CsKpisResponse>('cs_kpis', actionInput(params), { signal });
  }

  /** parent_account_detail — basic parent account info + child sfIds. */
  async getParentAccount(sfId: string): Promise<ParentAccountDetail> {
    return this.callAction<ParentAccountDetail>('parent_account_detail', { sfId });
  }

  /** parent_account_enrichment — enrichment aggregated across all children (account-admin). */
  async getParentAccountEnrichment(sfId: string): Promise<AccountEnrichment> {
    return this.callAction<AccountEnrichment>('parent_account_enrichment', { sfId });
  }

  /** fleet_health — fleet-wide aggregation (clinical / account-admin). */
  async getFleetHealth(): Promise<FleetHealthData> {
    return this.callAction<FleetHealthData>('fleet_health');
  }

  /** urgent_tickets — all open crit/high/urgent cases, or one priority. */
  async getUrgentTickets(priority?: string): Promise<{ tickets: UrgentTicket[] }> {
    return this.callAction<{ tickets: UrgentTicket[] }>('urgent_tickets', actionInput({ priority }));
  }

  /** success_board — Clinical Success Board metrics. */
  async getSuccessBoard(): Promise<SuccessBoardData> {
    return this.callAction<SuccessBoardData>('success_board');
  }

  /** territory_analysis — per-CSM territory analysis of contracted protocols. */
  async getTerritoryAnalysis(): Promise<TerritoryAnalysisData> {
    return this.callAction<TerritoryAnalysisData>('territory_analysis');
  }

  /** account_cleanup_alerts — the account AE's data-cleanup findings (account-admin). */
  async getAccountCleanupAlerts(sfId: string): Promise<AccountCleanupAlertsResponse> {
    return this.callAction<AccountCleanupAlertsResponse>('account_cleanup_alerts', { sfId });
  }

  /** PATCH /api/briefings/me/cleanup-alerts/:id — update finding status/severity. */
  async patchMyCleanupAlert(id: string, body: AccountCleanupAlertPatch): Promise<{ alert?: Record<string, unknown> }> {
    return this.requestWithBase<{ alert?: Record<string, unknown> }>(
      `/cleanup-alerts/${encodeURIComponent(id)}`,
      '/api/briefings/me',
      { method: 'PATCH', body: JSON.stringify(body) },
    );
  }

  /** list_snowflake_accounts — Snowflake source-tab list. 403 for viewers without the tab. */
  async listSnowflakeAccounts(params?: SnowflakeAccountsListParams): Promise<SnowflakeAccountsListResponse> {
    return this.callAction<SnowflakeAccountsListResponse>('list_snowflake_accounts', actionInput(params));
  }

  /** snowflake_account_detail — throws an ApiError with statusCode 404 / 403. */
  async getSnowflakeAccount(accountKey: number): Promise<SnowflakeAccountDetail> {
    return this.callAction<SnowflakeAccountDetail>('snowflake_account_detail', { accountKey });
  }

  // ── Scout API — CO-447/452/453. Gated by the `scout` flag + RBAC entitlement. ──
  /** POST /api/scout/research — AI business research; returns one sourced brief (+ runId). */
  async researchScout(input: { subject: string; intent?: 'person' | 'company' | 'auto'; accountSfId?: string; context?: { company?: string; title?: string; email?: string; champion?: boolean }; refresh?: boolean }): Promise<ScoutBrief> {
    return this.requestWithBase<ScoutBrief>('/research', '/api/scout', { method: 'POST', body: JSON.stringify(input) });
  }
  /** POST /api/scout/save — toggle `saved` on one of your own runs. */
  async saveScoutRun(runId: string, saved: boolean): Promise<{ runId: string; saved: boolean }> {
    return this.requestWithBase('/save', '/api/scout', { method: 'POST', body: JSON.stringify({ runId, saved }) });
  }
  /** GET /api/scout/history — your recent runs (newest-first). */
  async scoutHistory(savedOnly?: boolean): Promise<{ data: ScoutHistoryRow[]; pagination: { total: number; limit: number; offset: number; hasMore: boolean } }> {
    return this.requestWithBase(`/history${savedOnly ? '?savedOnly=true' : ''}`, '/api/scout');
  }
  /** GET /api/scout/runs/:id — one of your own runs (recents reopen path).
   *  Bypasses the GET cache so every reopen also bumps the recents entry server-side. */
  async getScoutRun(runId: string): Promise<ScoutHistoryRow> {
    this.clearCachePrefix('/api/scout/runs');
    return this.requestWithBase(`/runs/${encodeURIComponent(runId)}`, '/api/scout');
  }

  // ── Approval queue — "Needs you" (src/approvals, Phase 2 APR-01/02/05). ──
  // Every read is fresh: the queue changes under the reader (a meeting page settles a
  // proposal, a request expires), and the badge must agree with the list.
  /** GET /api/approvals?filter= — your queue, newest first (`pending` includes failed proposals). */
  async listApprovals(filter: ApprovalQueueFilter = 'pending', limit?: number): Promise<ApprovalView[]> {
    this.clearCachePrefix('/api/approvals');
    const qs = new URLSearchParams({ filter, ...(limit ? { limit: String(limit) } : {}) });
    return (await this.requestWithBase<ApprovalListResponse>(`?${qs.toString()}`, '/api/approvals')).data;
  }
  /** GET /api/approvals/count — pending + failed proposals, for the nav badge. */
  async getApprovalCount(): Promise<number> {
    this.clearCachePrefix('/api/approvals/count');
    return (await this.requestWithBase<ApprovalCountResponse>('/count', '/api/approvals')).pending;
  }
  /** GET /api/approvals/:id — one proposal with its lines ("Here's the information to update"). */
  async getApproval(id: string): Promise<ApprovalView> {
    this.clearCachePrefix(`/api/approvals/${id}`);
    return (await this.requestWithBase<ApprovalResponse>(`/${encodeURIComponent(id)}`, '/api/approvals')).approval;
  }
  /** PATCH /api/approvals/:id — APR-02 edits, keyed by line key (an `include` line takes a boolean). */
  async editApproval(id: string, values: Record<string, unknown>): Promise<ApprovalView> {
    return (await this.requestWithBase<ApprovalResponse>(`/${encodeURIComponent(id)}`, '/api/approvals', { method: 'PATCH', body: JSON.stringify({ values }) })).approval;
  }
  /** POST /api/approvals/:id/approve — applies the change once. A failed write rejects with `{ reason: 'apply_failed', approval }` (502). */
  async approveApproval(id: string): Promise<ApprovalView> {
    return (await this.requestWithBase<ApprovalResponse>(`/${encodeURIComponent(id)}/approve`, '/api/approvals', { method: 'POST' })).approval;
  }
  /** POST /api/approvals/:id/reject — nothing outside Compass changes; the feature is told. */
  async rejectApproval(id: string, reason?: string): Promise<ApprovalView> {
    return (await this.requestWithBase<ApprovalResponse>(`/${encodeURIComponent(id)}/reject`, '/api/approvals', { method: 'POST', body: JSON.stringify(reason ? { reason } : {}) })).approval;
  }

  // ── Notification inbox — the Header bell (src/notifications, Phase 2 OMN-01). ──
  // Your own inbox only; every read is fresh so the badge agrees with the list.
  /** GET /api/notifications?limit= — your recent items, newest first, with the unread count. */
  async listNotifications(limit = 20): Promise<NotificationListResponse> {
    this.clearCachePrefix('/api/notifications');
    return this.requestWithBase<NotificationListResponse>(`?limit=${encodeURIComponent(String(limit))}`, '/api/notifications');
  }
  /** GET /api/notifications/count — unread items, for the bell badge. */
  async getNotificationCount(): Promise<number> {
    this.clearCachePrefix('/api/notifications/count');
    return (await this.requestWithBase<NotificationCountResponse>('/count', '/api/notifications')).unread;
  }
  /** POST /api/notifications/:id/read — mark one of your items read. */
  async markNotificationRead(id: string): Promise<NotificationItemView> {
    return (await this.requestWithBase<NotificationReadResponse>(`/${encodeURIComponent(id)}/read`, '/api/notifications', { method: 'POST' })).item;
  }
  /** POST /api/notifications/read-all — mark every unread item read. */
  async markAllNotificationsRead(): Promise<number> {
    return (await this.requestWithBase<NotificationReadAllResponse>('/read-all', '/api/notifications', { method: 'POST' })).updated;
  }

  // ── Your SMS number (OMN-04; 404 unless `sms_notifications` is on). A refusal rejects with SmsErrorBody. ──
  /** GET /api/notifications/sms — your number and where it stands. */
  async getSmsConsent(): Promise<SmsConsentView> {
    this.clearCachePrefix('/api/notifications/sms');
    return (await this.requestWithBase<SmsConsentResponse>('/sms', '/api/notifications')).consent;
  }
  /** PUT /api/notifications/sms — add or change the number; Compass texts it a 6-digit code. */
  async startSmsVerification(phone: string): Promise<SmsConsentView> {
    return (await this.requestWithBase<SmsConsentResponse>('/sms', '/api/notifications', { method: 'PUT', body: JSON.stringify({ phone }) })).consent;
  }
  /** POST /api/notifications/sms/verify — the texted code; right = texts on. */
  async verifySmsNumber(code: string): Promise<SmsConsentView> {
    return (await this.requestWithBase<SmsConsentResponse>('/sms/verify', '/api/notifications', { method: 'POST', body: JSON.stringify({ code }) })).consent;
  }
  /** DELETE /api/notifications/sms — texts stop at once. */
  async removeSmsNumber(): Promise<SmsConsentView> {
    return (await this.requestWithBase<SmsConsentResponse>('/sms', '/api/notifications', { method: 'DELETE' })).consent;
  }

  // ── Calendar (src/calendar, Phase 2 CAL-01/03/04) — your own synced Google Calendar. ──
  /** GET /api/calendar/events?from&to — your events overlapping [from, to) with their Salesforce context; `availability` says why it may be empty. */
  async calendarEvents(from: string, to: string): Promise<CalendarEventsResponse> {
    this.clearCachePrefix('/api/calendar/events');
    return this.requestWithBase<CalendarEventsResponse>(`/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, '/api/calendar');
  }
  /** POST /api/calendar/events/:id/meeting — the Compass meeting for one of your events, made from it when there is none. */
  async calendarEventMeeting(id: string): Promise<CalendarEventMeetingResponse> {
    const out = await this.requestWithBase<CalendarEventMeetingResponse>(`/events/${encodeURIComponent(id)}/meeting`, '/api/calendar', { method: 'POST' });
    this.clearCachePrefix('/api/calendar');
    this.clearCachePrefix('/api/meetings');
    return out;
  }
  /** POST /api/calendar/events — New meeting (MTG-01 / MTG-02): an invite on your own Google Calendar, guests e-mailed. */
  async calendarCreateEvent(input: CalendarCreateEventRequest): Promise<CalendarCreateEventResponse> {
    const out = await this.requestWithBase<CalendarCreateEventResponse>('/events', '/api/calendar', { method: 'POST', body: JSON.stringify(input) });
    this.clearCachePrefix('/api/calendar');
    return out;
  }

  // ── Meetings API (docs/Features/Meetings, MT-143) — resource-shaped, no per-screen endpoints. ──
  // The typed per-resource wrappers live in client/src/pages/meetings/api/client.ts; these
  // two generic methods keep every call on the centralized client (cache, 401 handling).
  /** GET /api/meetings<path> */
  async meetingsGet<T>(path: string, opts: { fresh?: boolean } = {}): Promise<T> {
    if (opts.fresh) this.clearCachePrefix(`/api/meetings${path.split('?')[0]}`);
    return this.requestWithBase<T>(path, '/api/meetings');
  }
  /** POST / PATCH / PUT / DELETE /api/meetings<path> — invalidates the meetings cache. */
  async meetingsSend<T>(path: string, method: 'POST' | 'PATCH' | 'PUT' | 'DELETE', body?: unknown): Promise<T> {
    const out = await this.requestWithBase<T>(path, '/api/meetings', { method, ...(body !== undefined ? { body: JSON.stringify(body) } : {}) });
    this.clearCachePrefix('/api/meetings');
    return out;
  }
  /** Drop every cached meetings GET (after an intent the server acted on). */
  invalidateMeetings(): void {
    this.clearCachePrefix('/api/meetings');
  }
  /** SSE URL for "Ask CompassAI about this meeting" (requireCompassAi on the server). */
  getMeetingChatUrl(meetingId: string): string {
    return `/api/meetings/${encodeURIComponent(meetingId)}/chat`;
  }
  /** Raw chunk upload for a recording (MT-R2) — bytes, not JSON, so it bypasses `request()` on purpose. */
  meetingChunkUrl(meetingId: string, captureId: string, idx: number): string {
    return `/api/meetings/${encodeURIComponent(meetingId)}/captures/${encodeURIComponent(captureId)}/chunks/${idx}`;
  }
  /** Admin: GET /api/admin/meetings/config */
  async getMeetingsAdminConfig(): Promise<{ override: Record<string, unknown>; resolved: Record<string, unknown> }> {
    return this.request('/meetings/config');
  }
  /** Admin: PUT /api/admin/meetings/config */
  async updateMeetingsAdminConfig(cfg: Record<string, unknown>): Promise<{ override: Record<string, unknown>; resolved: Record<string, unknown> }> {
    return this.request('/meetings/config', { method: 'PUT', body: JSON.stringify(cfg) });
  }
  /** Admin: GET /api/admin/meetings/runs */
  async getMeetingsAdminRuns(params: { status?: string; limit?: number; offset?: number } = {}): Promise<{ data: Array<Record<string, unknown>>; pagination: { total: number; limit: number; offset: number } }> {
    const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => [k, String(v)]));
    this.clearCachePrefix('/api/admin/meetings/runs');
    return this.request(`/meetings/runs${q.toString() ? `?${q}` : ''}`);
  }
  /** Admin: GET /api/admin/meetings/usage */
  async getMeetingsAdminUsage(days = 30): Promise<{ days: number; data: Array<{ shell: string; panel: string; intent: string; count: number }> }> {
    this.clearCachePrefix('/api/admin/meetings/usage');
    return this.request(`/meetings/usage?days=${days}`);
  }

  // ── Page-view analytics (Admin Settings → Usage) ──
  /** Admin: GET /api/admin/usage/page-views — who opened which page, web vs app. Always fresh. */
  async getPageViewStats(q: Partial<PageViewStatsQuery> = {}): Promise<PageViewStatsResponse> {
    this.clearCachePrefix('/api/admin/usage');
    const params = new URLSearchParams();
    if (q.from) params.set('from', q.from);
    if (q.to) params.set('to', q.to);
    if (q.client) params.set('client', q.client);
    if (q.scope && q.scope !== 'all') params.set('scope', q.scope);
    if (q.team) params.set('team', q.team);
    return this.request(`/usage/page-views${params.toString() ? `?${params}` : ''}`);
  }
  /** Admin: GET /api/admin/usage/page-views/daily — the daily series for a person, a page, or both. Always fresh. */
  async getPageViewDaily(q: Partial<PageViewDailyQuery> = {}): Promise<PageViewDailyResponse> {
    this.clearCachePrefix('/api/admin/usage');
    const params = new URLSearchParams();
    if (q.from) params.set('from', q.from);
    if (q.to) params.set('to', q.to);
    if (q.client) params.set('client', q.client);
    if (q.userId) params.set('userId', q.userId);
    if (q.pageKey) params.set('pageKey', q.pageKey);
    if (q.scope && q.scope !== 'all') params.set('scope', q.scope);
    if (q.team) params.set('team', q.team);
    return this.request(`/usage/page-views/daily${params.toString() ? `?${params}` : ''}`);
  }
  /** Admin: URL of the usage report PDF for a window — everyone, or one person (`userId`). */
  getPageViewReportUrl(q: Partial<PageViewReportQuery> = {}): string {
    const params = new URLSearchParams();
    if (q.from) params.set('from', q.from);
    if (q.to) params.set('to', q.to);
    if (q.client) params.set('client', q.client);
    if (q.userId) params.set('userId', q.userId);
    if (q.scope && q.scope !== 'all') params.set('scope', q.scope);
    if (q.team) params.set('team', q.team);
    return `/api/admin/usage/page-views/report.pdf${params.toString() ? `?${params}` : ''}`;
  }
  /** Admin: GET /api/admin/progress — delivery progress per project (docs/Progress/*.json). Always fresh. */
  async getProgress(): Promise<ProgressResponse> {
    this.clearCachePrefix('/api/admin/progress');
    return this.request('/progress');
  }
  /** Admin: URL that opens a plan doc a progress project links to, optionally at a section anchor. */
  getProgressDocUrl(docPath: string, anchor?: string): string {
    return `/api/admin/progress/doc?path=${encodeURIComponent(docPath)}${anchor ? `#${anchor}` : ''}`;
  }
  /** Admin: GET /api/admin/usage/page-views/users/:id — one person's most recent page views. */
  async getUserPageViews(userId: string, scope: PageViewScope = 'all'): Promise<UserPageViewsResponse> {
    this.clearCachePrefix('/api/admin/usage');
    return this.request(`/usage/page-views/users/${encodeURIComponent(userId)}${scope === 'all' ? '' : `?scope=${scope}`}`);
  }

  // ── Recents API — per-user "recently opened" lists (docs/Features/Recent). ──
  /** GET /api/recents/:kind — your list for one kind, newest-first. Always
   *  fetched fresh: views are recorded by other GETs (account detail, scout
   *  research), which the mutation-based cache invalidation can't see. */
  async listRecents(kind: string): Promise<{ data: RecentItem[] }> {
    this.clearCachePrefix('/api/recents');
    return this.requestWithBase(`/${encodeURIComponent(kind)}`, '/api/recents');
  }
  /** DELETE /api/recents/:kind/:key — remove one item from your list. */
  async removeRecent(kind: string, key: string): Promise<void> {
    return this.requestWithBase(`/${encodeURIComponent(kind)}/${encodeURIComponent(key)}`, '/api/recents', { method: 'DELETE' });
  }
  /** DELETE /api/recents/:kind — clear your list for one kind. */
  async clearRecents(kind: string): Promise<void> {
    return this.requestWithBase(`/${encodeURIComponent(kind)}`, '/api/recents', { method: 'DELETE' });
  }
  /** Admin: GET /api/admin/scout/config — raw override + resolved (with guardrails). */
  async getScoutConfig(): Promise<{ override: ScoutConfigOverride; resolved: ScoutResolvedConfig }> {
    return this.request('/scout/config');
  }
  /** Admin: PUT /api/admin/scout/config */
  async updateScoutConfig(cfg: ScoutConfigOverride): Promise<{ override: ScoutConfigOverride; resolved: ScoutResolvedConfig }> {
    return this.request('/scout/config', { method: 'PUT', body: JSON.stringify(cfg) });
  }
  /** Admin: GET /api/admin/scout/runs — the run-log viewer. */
  async listScoutRuns(params?: { q?: string; outcome?: string; savedOnly?: boolean; limit?: number; offset?: number }): Promise<{ data: ScoutRunRow[]; pagination: { total: number; limit: number; offset: number; hasMore: boolean } }> {
    const qs = new URLSearchParams();
    if (params) for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') qs.append(k, String(v));
    const q = qs.toString() ? `?${qs.toString()}` : '';
    return this.request(`/scout/runs${q}`);
  }

  // ── AI Knowledge (OKF) ──
  /** Admin: GET /api/admin/okf/docs — all OKF docs (generated + override + active body). */
  async listOkfDocs(): Promise<{ data: OkfDocView[] }> {
    return this.request('/okf/docs');
  }
  /** Admin: POST /api/admin/okf/regenerate — (re)generate generated bodies. */
  async regenerateOkf(slug?: string): Promise<{ regenerated: number; data: OkfDocView[] }> {
    return this.request(`/okf/regenerate${slug ? `?slug=${encodeURIComponent(slug)}` : ''}`, { method: 'POST' });
  }
  /** Admin: PUT /api/admin/okf/docs/:id — set or clear the admin override body. */
  async setOkfOverride(id: string, override: string | null): Promise<{ data: OkfDocView[] }> {
    return this.request(`/okf/docs/${id}`, { method: 'PUT', body: JSON.stringify({ override }) });
  }
  /** Any user: POST /api/ai/context — the composed AI Knowledge context. */
  async getAiContext(): Promise<{ context: string }> {
    return this.requestWithBase('/context', '/api/ai', { method: 'POST' });
  }

  // ──────────────────────────────────────────────────────────
  // Create-Dashboards (CO-74)
  // Endpoints under /api/dashboards/*
  // ──────────────────────────────────────────────────────────
  // CO-104: server returns `hiddenCount` for non-bypass roles — the
  // number of files dropped by RBAC visibility before pagination.
  // Used by FileBrowser to render "X files are hidden by your access
  // level" so the user understands the list isn't the full corpus.
  async listUploadedFiles(params?: { offset?: number; limit?: number; category?: string }): Promise<{ data: UploadedFile[]; total: number; hiddenCount?: number; storageBackend?: 's3-dev' | 's3-prod' | 'disk' }> {
    const qs = new URLSearchParams();
    if (params?.offset !== undefined) qs.append('offset', String(params.offset));
    if (params?.limit !== undefined) qs.append('limit', String(params.limit));
    if (params?.category) qs.append('category', params.category);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.requestWithBase(`/files${suffix}`, '/api/dashboards');
  }

  async getUploadedFile(id: string): Promise<{ data: UploadedFile }> {
    return this.requestWithBase(`/files/${id}`, '/api/dashboards');
  }

  async getFileSchema(id: string): Promise<{
    columns: string[];
    rowCount: number;
    sample: Record<string, unknown>[];
    notes: string[];
    extractedText: string;
  }> {
    return this.requestWithBase(`/files/${id}/schema`, '/api/dashboards');
  }

  /**
   * Multipart batch upload. The signature accepts a list of (File + metadata)
   * pairs so the caller doesn't have to hand-build the FormData.
   */
  async uploadDashboardFiles(items: Array<{ file: File; dataAsOfDate: string; categories?: string[]; reviewExistingDashboards?: boolean }>): Promise<{ data: StagedFile[]; total: number }> {
    const form = new FormData();
    for (const item of items) {
      form.append('files', item.file);
    }
    const response = await fetch('/api/dashboards/create', { method: 'POST', body: form });
    if (response.status === 401) {
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
      throw err;
    }
    this.clearCachePrefix('/api/dashboards');
    return response.json();
  }

  /**
   * XHR-backed upload that surfaces granular progress via `onProgress`.
   * Returns staging descriptors (no DB row yet — use commitStagedFiles).
   */
  uploadDashboardFilesWithProgress(
    items: Array<{ file: File; dataAsOfDate: string; categories?: string[]; reviewExistingDashboards?: boolean }>,
    onProgress: (loaded: number, total: number) => void,
  ): Promise<{ data: StagedFile[]; total: number }> {
    return new Promise((resolve, reject) => {
      const form = new FormData();
      for (const item of items) form.append('files', item.file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/api/dashboards/create', true);
      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) onProgress(evt.loaded, evt.total);
      };
      xhr.onload = () => {
        if (xhr.status === 401) {
          window.location.href = '/login';
          reject(new Error('Unauthorized'));
          return;
        }
        try {
          const body = JSON.parse(xhr.responseText || '{}');
          if (xhr.status >= 200 && xhr.status < 300) {
            this.clearCachePrefix('/api/dashboards');
            resolve(body);
          } else {
            reject(body);
          }
        } catch {
          reject(new Error('Server returned an unexpected response. The server may be down or restarting — please try again.'));
        }
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.send(form);
    });
  }

  /** SSE URL for the per-requirement generation event stream. */
  getRequirementEventsUrl(requirementId: string): string {
    return `/api/dashboards/requirements/${encodeURIComponent(requirementId)}/events`;
  }

  /**
   * Tail the PM2 log file that the prod Compass process writes to.
   * Admin-only; returns `{ file, stream, lines, content, note? }`.
   */
  async getProdLog(stream: 'out' | 'error', lines: number): Promise<{ data: { file: string; stream: 'out' | 'error'; lines: number; content: string; note?: string } }> {
    // Bypass the 5-minute GET cache; the user explicitly wants fresh logs.
    this.clearCachePrefix('/api/admin/prod-log');
    return this.request(`/prod-log?stream=${stream}&lines=${lines}`);
  }

  /**
   * In-process log buffer — populated when the dev backend is running
   * and the developer can't see the terminal it logs to.
   */
  async getLocalLog(lines: number): Promise<{ data: { lines: number; bufferSize: number; content: string; note?: string } }> {
    this.clearCachePrefix('/api/admin/local-log');
    return this.request(`/local-log?lines=${lines}`);
  }

  async classifyUploadedFile(id: string, categories: string[]): Promise<{ data: UploadedFile }> {
    return this.requestWithBase(`/files/${id}/classify`, '/api/dashboards', {
      method: 'PATCH',
      body: JSON.stringify({ categories }),
    });
  }

  /**
   * CO-110: ask the backend to re-run the AI classifier and return its
   * suggested categories without persisting. The caller should write
   * the suggestion back via {@link classifyUploadedFile} once the user
   * confirms.
   */
  async deleteUploadedFile(id: string): Promise<void> {
    return this.requestWithBase(`/files/${id}`, '/api/dashboards', { method: 'DELETE' });
  }

  /**
   * Promote staged files to permanent storage. Called when the user
   * clicks "Done" in the upload dialog after picking categories.
   *
   * `conflictResolution` is set when the upload collided with an
   * existing file — `overwrite` replaces the row identified by
   * `existingFileId` (preserving the file id so dashboards using it
   * keep working); `rename` lets the server pick the next available
   * `name (N).ext` and create a new row.
   */
  async commitStagedFiles(files: Array<{
    stagingId: string;
    originalFilename: string;
    fileSize: number;
    fileType: string;
    dataAsOfDate: string;
    categories: string[];
    reviewExistingDashboards?: boolean;
    conflictResolution?: 'overwrite' | 'rename';
    existingFileId?: string;
  }>): Promise<{ data: UploadedFile[]; total: number }> {
    return this.requestWithBase('/files/commit', '/api/dashboards', {
      method: 'POST',
      body: JSON.stringify({ files }),
    });
  }

  /** Delete a single staging file (cancel / remove row from dialog). */
  async deleteStagedFile(stagingId: string): Promise<void> {
    return this.requestWithBase(`/files/staging/${encodeURIComponent(stagingId)}`, '/api/dashboards', { method: 'DELETE' });
  }

  // CO-104: same `hiddenCount` semantics as listUploadedFiles — a
  // dashboard is hidden when even one of its source files is out of
  // RBAC reach for the caller.
  async listDashboards(): Promise<{ data: Dashboard[]; total: number; hiddenCount?: number }> {
    return this.requestWithBase('/', '/api/dashboards');
  }

  /** CO-111: metadata-only fetch for the viewer's category controls. */
  async getDashboardMeta(id: string): Promise<{ data: Dashboard }> {
    return this.requestWithBase(`/${id}/meta`, '/api/dashboards');
  }

  /**
   * CO-119: URL for the raw HTML download. Server sets
   * Content-Disposition: attachment so the browser saves the file
   * rather than navigating to it. No Compass-specific injection on
   * this path — the downloaded file is exactly what's on disk.
   */
  getDashboardDownloadUrl(id: string): string {
    return `/api/dashboards/${encodeURIComponent(id)}/download`;
  }

  async deleteDashboard(id: string): Promise<void> {
    return this.requestWithBase(`/${id}`, '/api/dashboards', { method: 'DELETE' });
  }

  /**
   * CO-111: admin-only override of a dashboard's effective categories.
   * Pass an empty array to clear the override.
   */
  async updateDashboardCategoryOverride(
    id: string,
    categories: string[],
  ): Promise<{ data: Dashboard }> {
    return this.requestWithBase(`/${id}/categories`, '/api/dashboards', {
      method: 'PATCH',
      body: JSON.stringify({ categories }),
    });
  }

  /**
   * URL for the dashboard chat endpoint. Streamed via fetch (SSE-like
   * lines) — POST means we can't use EventSource, which is GET-only.
   */
  getDashboardChatUrl(id: string): string {
    return `/api/dashboards/${id}/chat`;
  }

  /** SSE URL for the multi-file chat — POST { fileIds, messages }. */
  getFilesChatUrl(): string {
    return '/api/dashboards/chat';
  }

  /** SSE URL for "Ask AI about this account" — POST { messages, conversationId? }. */
  getAccountChatUrl(sfId: string): string {
    return `/api/accounts/${encodeURIComponent(sfId)}/chat`;
  }

  /** SSE URL for the portfolio-level "Ask CompassAI" on the Accounts / Parent
   *  Accounts list pages — POST { messages, conversationId?, mode }. */
  getPortfolioChatUrl(): string {
    return '/api/accounts/chat';
  }

  /** SSE URL for the Weekly Briefing chat — POST { view, week?, messages, conversationId? }.
   *  `week` is the report date the page is showing; the server grounds the
   *  answer in that person/week's briefing (src/services/ai-chat/briefing-chat.ts). */
  getBriefingChatUrl(): string {
    return '/api/ai/chat';
  }

  /** SSE URL for the app-wide CompassAI assistant — POST { question, context?, history, conversationId? }. */
  getGlobalChatUrl(): string {
    return '/api/ai/ask';
  }

  // ── Generic AI conversation history (Accounts + Briefings sidebar) ──
  async listAiConversations(surface: AiChatSurface, refId?: string): Promise<{ conversations: AiConversationHeader[] }> {
    const params = new URLSearchParams({ surface });
    if (refId !== undefined) params.set('refId', refId);
    return this.requestWithBase(`/conversations?${params.toString()}`, '/api/ai');
  }

  /**
   * UNIFIED cross-surface history: every one of the caller's own CompassAI
   * threads across all surfaces, newest first, each carrying its surface + refId
   * (GET /conversations?surface=all). Backs the "All history" toggle.
   */
  async listAllAiConversations(): Promise<{ conversations: AiConversationHeaderWithContext[] }> {
    return this.requestWithBase(`/conversations?surface=all`, '/api/ai');
  }

  async getAiConversation(id: string): Promise<AiConversationDetail> {
    return this.requestWithBase(`/conversations/${id}`, '/api/ai');
  }

  async renameAiConversation(id: string, title: string): Promise<void> {
    return this.requestWithBase(`/conversations/${id}`, '/api/ai', { method: 'PATCH', body: JSON.stringify({ title }) });
  }

  async deleteAiConversation(id: string): Promise<void> {
    return this.requestWithBase(`/conversations/${id}`, '/api/ai', { method: 'DELETE' });
  }

  // ── Personal Space page (/api/personal, contract src/contracts/personal-space.ts) ──
  // The caller's own space only; 404 code personal_space_browser_off while the flag is off,
  // 403 code personal_space_private while an admin views as the user.
  async personalOverview(): Promise<PersonalOverview> {
    return this.requestWithBase<PersonalOverview>('/overview', '/api/personal');
  }

  async personalItems(q: PersonalItemsQuery): Promise<PersonalItemsResponse> {
    const p = new URLSearchParams();
    if (q.q) p.set('q', q.q);
    if (q.kinds?.length) p.set('kinds', q.kinds.join(','));
    if (q.sources?.length) p.set('sources', q.sources.join(','));
    if (q.from) p.set('from', q.from);
    if (q.to) p.set('to', q.to);
    if (q.person) p.set('person', q.person);
    if (q.domain) p.set('domain', q.domain);
    if (q.account) p.set('account', q.account);
    if (q.limit) p.set('limit', String(q.limit));
    if (q.order) p.set('order', q.order);
    const s = p.toString();
    return this.requestWithBase<PersonalItemsResponse>(`/items${s ? `?${s}` : ''}`, '/api/personal');
  }

  async personalItem(itemId: string): Promise<PersonalItemDetailResponse> {
    return this.requestWithBase<PersonalItemDetailResponse>(`/items/${encodeURIComponent(itemId)}`, '/api/personal');
  }

  async personalPeople(q?: string, limit = 25): Promise<PersonalPeopleResponse> {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    p.set('limit', String(limit));
    return this.requestWithBase<PersonalPeopleResponse>(`/people?${p.toString()}`, '/api/personal');
  }

  /** Rate one assistant answer (ledger turn). Upserts the caller's feedback. */
  async sendAiFeedback(turnId: string, body: { rating: 1 | -1; reason?: string; note?: string }): Promise<void> {
    await this.requestWithBase(`/turns/${turnId}/feedback`, '/api/ai', { method: 'POST', body: JSON.stringify(body) });
  }

  /** Remove the caller's rating on an assistant answer. */
  async clearAiFeedback(turnId: string): Promise<void> {
    await this.requestWithBase(`/turns/${turnId}/feedback`, '/api/ai', { method: 'DELETE' });
  }

  /**
   * SSE URL for the Smart Query endpoint scoped to a single dashboard —
   * POST { question, conversationId? }. Backend routes the question to
   * SQL / RAG / hybrid / unclear and streams provenance + tokens.
   * Gated on the `smart_query_enabled` feature flag.
   */
  getSmartQueryDashboardUrl(id: string): string {
    return `/api/dashboards/${id}/smart-query`;
  }

  /** SSE URL for the multi-file Smart Query — POST { question, fileIds, conversationId? }. */
  getSmartQueryFilesUrl(): string {
    return '/api/dashboards/smart-query';
  }

  // ── Smart Query conversation history (sidebar UI) ──

  /**
   * List the current user's Smart Query conversations, newest first.
   * `dashboardId` filters to a single dashboard's chat history.
   */
  async listSmartQueryConversations(opts: {
    dashboardId?: string;
    limit?: number;
    before?: string;
  } = {}): Promise<{ conversations: SmartQueryConversationHeader[] }> {
    const params = new URLSearchParams();
    if (opts.dashboardId) params.set('dashboardId', opts.dashboardId);
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.before) params.set('before', opts.before);
    const qs = params.toString();
    return this.requestWithBase(`/smart-query/conversations${qs ? `?${qs}` : ''}`, '/api/dashboards');
  }

  /** Hydrate a full conversation (header + all turns) for the chat panel. */
  async getSmartQueryConversation(id: string): Promise<SmartQueryConversationDetail> {
    return this.requestWithBase(`/smart-query/conversations/${id}`, '/api/dashboards');
  }

  async renameSmartQueryConversation(
    id: string,
    title: string,
  ): Promise<SmartQueryConversationHeader> {
    return this.requestWithBase(`/smart-query/conversations/${id}`, '/api/dashboards', {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    });
  }

  async deleteSmartQueryConversation(id: string): Promise<void> {
    return this.requestWithBase(`/smart-query/conversations/${id}`, '/api/dashboards', {
      method: 'DELETE',
    });
  }

  async createDashboardRequirement(input: { requirementsText: string; fileIds: string[]; reviewExistingDashboards?: boolean }): Promise<{ data: { requirement: any; dashboard: any; generationMs: number; summary: string } }> {
    return this.requestWithBase('/requirements', '/api/dashboards', {
      method: 'POST',
      body: JSON.stringify({ ...input, reviewExistingDashboards: input.reviewExistingDashboards ?? false }),
    });
  }

  async listDashboardRequirements(): Promise<{ data: any[]; total: number }> {
    return this.requestWithBase('/requirements', '/api/dashboards');
  }

  // ──────────────────────────────────────────────────────────
  // Admin File Browser
  // Filesystem CRUD over data-files/. All admin-only; gated by
  // requireAdmin at /api/admin (see app.ts).
  // ──────────────────────────────────────────────────────────
  async listFileBrowserDir(relPath: string): Promise<{ data: FileBrowserDirListing }> {
    // Bypass the GET cache so navigation reflects current FS state.
    this.clearCachePrefix('/api/admin/file-browser/list');
    const qs = relPath ? `?path=${encodeURIComponent(relPath)}` : '';
    return this.request(`/file-browser/list${qs}`);
  }

  async statFileBrowserEntry(relPath: string, opts?: { hash?: boolean }): Promise<{ data: FileBrowserStat }> {
    this.clearCachePrefix('/api/admin/file-browser/stat');
    const params = new URLSearchParams();
    params.append('path', relPath);
    if (opts?.hash) params.append('hash', 'true');
    return this.request(`/file-browser/stat?${params.toString()}`);
  }

  fileBrowserDownloadUrl(relPath: string): string {
    return `/api/admin/file-browser/download?path=${encodeURIComponent(relPath)}`;
  }

  async fileBrowserMkdir(relPath: string): Promise<{ data: FileBrowserEntry }> {
    return this.request('/file-browser/mkdir', { method: 'POST', body: JSON.stringify({ path: relPath }) });
  }

  async fileBrowserRename(relPath: string, newName: string): Promise<{ data: { from: string; to: string } }> {
    return this.request('/file-browser/rename', {
      method: 'PATCH',
      body: JSON.stringify({ path: relPath, newName }),
    });
  }

  async fileBrowserMove(
    relPath: string,
    destination: string,
    overwrite = false,
  ): Promise<{ data: { from: string; to: string; destination: string } }> {
    return this.request('/file-browser/move', {
      method: 'PATCH',
      body: JSON.stringify({ path: relPath, destination, overwrite }),
    });
  }

  async fileBrowserDelete(relPath: string, recursive = false): Promise<void> {
    return this.request('/file-browser/delete', {
      method: 'DELETE',
      body: JSON.stringify({ path: relPath, recursive }),
    });
  }

  // ──────────────────────────────────────────────────────
  // Weekly Briefings + My Salesforce reads (registry actions, /api/actions/<name>); downloads and writes stay on /api/briefings and /api/hygiene
  // ──────────────────────────────────────────────────────
  async getBriefingSuggestedQuestions(view: SuggestedQuestionsView): Promise<SuggestedQuestionsResponse> {
    return this.callAction<SuggestedQuestionsResponse>('briefing_suggested_questions', { view });
  }

  /**
   * The briefing for the authenticated caller (Phase C1; post-G7 the
   * principal comes from `buildPrincipal`, not stub identities). Tier
   * is derived server-side from the principal's position in the SF
   * hierarchy — there is no `tier` parameter to spoof. With no
   * person/week query params, the server renders the principal's own
   * latest-week briefing.
   *
   * Response shape extends `Briefing` with a `_meta` block that the
   * UI uses to render a "You are: X — tier: Y" banner.
   *
   * Rejects with an ApiError: 403 (no SF identity / policy denial, reason in
   * `message`), 404 (`details.code === 'BRIEFING_NOT_GENERATED'`), 404
   * `feature_disabled` when the flag is off.
   */
  async getMyBriefing(opts: { person?: string; week?: string } = {}): Promise<
    Briefing & {
      _meta: {
        principal: {
          name: string;
          sfUserId: string | null;
          isBypass: boolean;
        };
        derivedTier: BriefingTier;
        viewedAs: {
          personId: string;
          name: string;
          role: 'exec' | 'rvp' | 'ae';
          week: string;
        } | null;
        availableWeeks: string[];
        scopeKind: 'unrestricted' | 'denied' | 'in_set' | 'and' | 'or';
      };
    }
  > {
    return this.callAction('my_briefing', actionInput(opts));
  }

  /**
   * Build a URL for the current briefing scope.
   *
   *   - `disposition='attachment'` (default) — a save-on-click URL
   *     for the export popover's HTML option. The server sets
   *     `Content-Disposition: attachment`.
   *   - `disposition='inline'` — the same HTML rendered in-window
   *     (no attachment header). Used by the export popover's PDF
   *     option: open this in a new window, then `window.print()`.
   *
   * Same scope/RBAC checks as `getMyBriefing`.
   */
  getMyBriefingDownloadUrl(opts: {
    person?: string;
    week?: string;
    format?: 'html' | 'email' | 'json';
    disposition?: 'attachment' | 'inline';
  } = {}): string {
    const params = new URLSearchParams();
    if (opts.person) params.set('person', opts.person);
    if (opts.week) params.set('week', opts.week);
    params.set('format', opts.format ?? 'html');
    if (opts.disposition && opts.disposition !== 'attachment') {
      params.set('disposition', opts.disposition);
    }
    return `/api/briefings/me/download?${params.toString()}`;
  }

  /**
   * Download URL for a hygiene-report email HTML file. Use `ownerSfId='me'` for
   * the caller's own score, or pass a specific Salesforce User.Id for the
   * manager-facing roster drill-down path.
   */
  getHygieneDownloadUrl(ownerSfId: string): string {
    if (ownerSfId === 'me') return '/api/hygiene/me/download';
    return `/api/hygiene/roster/${encodeURIComponent(ownerSfId)}/download`;
  }

  /**
   * RBAC-scoped people + weeks list for the two-pane Briefings UI.
   * Drives the left-pane person dropdown and week list.
   *
   * Post-Phase-G7: items are keyed on Salesforce User.Id; subtree is
   * derived from the live SF manager chain. Throws 403 when the user
   * has no SF identity linked (`buildPrincipal` returns null in that
   * case) or when the policy denies (missing sales/financial role).
   * Successful responses always carry a populated `principal.sfUserId`.
   */
  async listMyPeople(): Promise<{
    items: Array<{
      id: string;
      name: string;
      role: 'exec' | 'rvp' | 'ae';
      parentId: string | null;
      weeks: Array<{ date: string; criticalCount: number; flaggedCount: number }>;
    }>;
    meta: {
      weekDates: string[];
      principal: {
        name: string;
        sfUserId: string | null;
      };
    };
  }> {
    return this.callAction('briefing_people');
  }

  /** my_hygiene_score — the caller's own hygiene score (unscored when there is no Salesforce owner). */
  async getMyHygieneScore(): Promise<HygieneScore> {
    return this.callAction<HygieneScore>('my_hygiene_score', {}, { fresh: true });
  }
  /** team_hygiene_roster — reps in the caller's oversight scope (empty for an AE). */
  async getTeamHygieneRoster(): Promise<RosterResponse> {
    return this.callAction<RosterResponse>('team_hygiene_roster', {}, { fresh: true });
  }
  /** rep_hygiene_score — one in-scope rep; rejects with statusCode 403 outside the caller's scope. */
  async getRepHygieneScore(ownerSfId: string): Promise<HygieneScore> {
    return this.callAction<HygieneScore>('rep_hygiene_score', { ownerSfId }, { fresh: true });
  }
  /** cleanup_alerts — Data Cleanup Scanner findings in the caller's owner scope. */
  async listMyCleanupAlerts(opts: { section?: string } = {}): Promise<{ alerts: CleanupAlert[] }> {
    return this.callAction<{ alerts: CleanupAlert[] }>('cleanup_alerts', actionInput(opts), { fresh: true });
  }
  /** cleanup_checkpoints — the static checkpoint catalog (with detection SQL). */
  async getCleanupCheckpoints(): Promise<{ checkpoints: CleanupCheckpointInfo[] }> {
    return this.callAction<{ checkpoints: CleanupCheckpointInfo[] }>('cleanup_checkpoints', {}, { fresh: true });
  }
  /** cleanup_alert_comments — one finding's comments; 404 outside the caller's scope. */
  async getCleanupAlertComments(alertId: string): Promise<{ comments: AlertComment[] }> {
    return this.callAction<{ comments: AlertComment[] }>('cleanup_alert_comments', { id: alertId }, { fresh: true });
  }
  /** cleanup_scan_status — the latest global rescan run; polled, so never cached. */
  async getCleanupScanStatus(): Promise<{ scan: CleanupScanRun | null }> {
    return this.callAction<{ scan: CleanupScanRun | null }>('cleanup_scan_status', {}, { fresh: true });
  }
  /** cleanup_alert_form_options — owner and open-deal names for the manual finding form. */
  async getCleanupAlertFormOptions(): Promise<{ owners: string[]; deals: string[] }> {
    return this.callAction<{ owners: string[]; deals: string[] }>('cleanup_alert_form_options', {}, { fresh: true });
  }

  /**
   * Force a fresh briefing for (personSfId, reportDate) — admin-only.
   *
   * Page-view loads are cache-read; an empty cache means the briefing
   * page renders an empty-state placeholder. This endpoint is the
   * manual writer: it calls `runTrackedBriefing` with
   * `triggeredBy='admin_regenerate'`, persists a new `BriefingRun`
   * row, and returns the assembled payload. Subsequent page views of
   * the same (person, week) read the regenerated payload from cache.
   *
   * Throws on 403 (non-admin) or 404 (unknown SF user / tier failure
   * — the server records its own failure row first).
   */
  async regenerateBriefing(opts: {
    personSfId: string;
    reportDate: string;
    sendEmail?: boolean;
  }): Promise<{
    runId: string;
    briefing: Briefing;
    viewedAs: { personId: string; name: string; role: BriefingTier; week: string };
  }> {
    this.clearCachePrefix('/api/actions/my_briefing');
    this.clearCachePrefix('/api/actions/briefing_people');
    this.clearCachePrefix('/api/admin/wbr-runs');
    return this.requestWithBase(
      '/wbr-runs/regenerate',
      '/api/admin',
      {
        method: 'POST',
        body: JSON.stringify(opts),
      },
    );
  }

  /** Send the email for an existing briefing run without regenerating content. */
  async sendBriefingEmail(opts: {
    personSfId: string;
    reportDate: string;
  }): Promise<{
    runId: string;
    viewedAs: { personId: string; name: string; role: BriefingTier; week: string };
  }> {
    this.clearCachePrefix('/api/admin/wbr-runs');
    return this.requestWithBase(
      '/wbr-runs/send-email',
      '/api/admin',
      {
        method: 'POST',
        body: JSON.stringify(opts),
      },
    );
  }

  /** Enqueue briefings for ALL configured WBR recipients for a given Monday.
   *  Uses the queued (async) cron-trigger path so the HTTP call returns quickly
   *  and jobs appear in Run History as they complete. `force: true` re-generates
   *  even if a briefing already exists for that week.
   *  `skipDelivery: true` generates content without sending Slack/email. */
  async triggerWbrAll(week: string, opts?: { skipDelivery?: boolean }): Promise<{ recipientsEnqueued: number; week: string }> {
    this.clearCachePrefix('/api/admin/wbr-runs');
    return this.requestWithBase(
      '/wbr/cron-trigger',
      '/api/admin',
      {
        method: 'POST',
        body: JSON.stringify({ week, inline: false, force: true, skipDelivery: opts?.skipDelivery ?? false }),
      },
    );
  }

  /** Send briefing emails for ALL configured WBR recipients for a given Monday
   *  using their existing persisted runs — no content regeneration. */
  async sendBriefingEmailAll(opts: {
    reportDate: string;
  }): Promise<{ sent: number; skipped: number; failed: number; week: string }> {
    this.clearCachePrefix('/api/admin/wbr-runs');
    return this.requestWithBase(
      '/wbr-runs/send-email-all',
      '/api/admin',
      {
        method: 'POST',
        body: JSON.stringify(opts),
      },
    );
  }
}

// ──────────────────────────────────────────────────────────
// Smart Query conversation history (Phase 3 sidebar)
// ──────────────────────────────────────────────────────────

export interface SmartQueryConversationHeader {
  id: string;
  userId: string;
  dashboardId: string | null;
  title: string;
  lastTurnAt: string;
  turnCount: number;
  createdAt: string;
}

export interface SmartQueryChunkRef {
  fileId: string;
  chunkIndex: number;
  distance: number;
}

export interface SmartQueryConversationTurn {
  id: string;
  conversationId: string;
  userId: string;
  dashboardId: string | null;
  turnIndex: number;
  question: string;
  intent: 'sql' | 'rag' | 'hybrid' | 'unclear';
  sql: string | null;
  tablesUsed: string[];
  rowCount: number | null;
  sampleRows: Array<Record<string, unknown>> | null;
  chunkRefs: SmartQueryChunkRef[] | null;
  answerText: string | null;
  costTokens: number;
  createdAt: string;
}

export interface SmartQueryConversationDetail extends SmartQueryConversationHeader {
  turns: SmartQueryConversationTurn[];
}

// Generic AI conversation history (Accounts + Briefings)
/** Client mirror of the server AI_CHAT_SURFACES union (src/services/ai-chat/conversations.ts). */
export type AiChatSurface = 'account' | 'briefing' | 'global' | 'portfolio' | 'meeting';

export interface AiConversationHeader {
  id: string;
  title: string;
  lastTurnAt: string;
  turnCount: number;
}
export interface AiConversationDetail extends AiConversationHeader {
  turns: { role: 'user' | 'assistant'; content: string }[];
}
/**
 * A conversation header carrying the surface + refId that produced it — the
 * shape returned by GET /conversations?surface=all (the unified view). The
 * unified history rail maps (surface, refId) → a bucket badge + a source route
 * via conversationContext(). refId is null for context-free threads (e.g. the
 * /compass-ai page's global thread).
 */
export interface AiConversationHeaderWithContext extends AiConversationHeader {
  surface: AiChatSurface;
  refId: string | null;
}

// ──────────────────────────────────────────────────────────
// Create-Dashboards (CO-74) types
// ──────────────────────────────────────────────────────────
export interface UploadedFile {
  id: string;
  filename: string;
  originalFilename: string;
  fileType: string;
  fileSize: number;
  storagePath: string;
  uploaderId: string;
  categories: string[];
  classificationSource: 'manual' | 'ai-suggested' | 'ai-confirmed';
  dataAsOfDate: string;
  createdAt: string;
  uploader?: { id: string; displayName: string; email: string };
  // Smart Query Phase 3 readiness signals — populated by the fire-and-forget
  // ClickHouse ingest + embedding pipeline. UI uses these to render a small
  // badge so users know when a freshly-uploaded file is queryable / cite-able.
  ingestStatus?: 'pending' | 'success' | 'failed' | 'skipped';
  embeddingStatus?: 'not_applicable' | 'pending' | 'success' | 'failed';
}

/** Lightweight descriptor returned by the upload (staging) endpoint. */
export interface StagedFile {
  stagingId: string;
  originalFilename: string;
  fileSize: number;
  fileType: string;
  /**
   * Set when this upload's filename collides with an existing file the
   * uploader can see. The dialog renders an overwrite-or-rename prompt
   * and the user's choice is sent back to the commit endpoint.
   */
  duplicate?: StagedDuplicate;
}

/** Conflict info for a staged file whose name matches an existing file. */
export interface StagedDuplicate {
  existingFileId: string;
  existingOriginalFilename: string;
  existingFileSize: number;
  existingCategories: string[];
  existingDataAsOfDate: string;
  existingCreatedAt: string;
  existingUploaderName: string;
  /** Suggested name if the user picks "rename" — e.g. `report (1).xls`. */
  proposedRenamedFilename: string;
}

export interface Dashboard {
  id: string;
  title: string;
  htmlPath: string;
  requirementId: string;
  requestorId: string;
  categories: string[];
  /** CO-111: admin-managed category override. Empty array = no override. */
  categoryOverride: string[];
  categoryOverrideAt: string | null;
  categoryOverrideBy: string | null;
  generatedAt: string;
  generationMs: number | null;
  /**
   * CO-119: which generator produced the dashboard. 'spec' for structured
   * renderer output (use case #2, or fallback path), 'html' for free-form
   * HTML emitted directly by the model (use case #1 with the
   * dashboards.upload_html_mode flag on).
   */
  generationMode: 'spec' | 'html';
  requestor?: { id: string; displayName: string; email: string };
  sourceFiles?: Array<{
    fileId: string;
    file: { id: string; originalFilename: string; categories?: string[] };
  }>;
  requirement?: { requirementsText: string };
}

// ──────────────────────────────────────────────────────────
// Admin File Browser types
// ──────────────────────────────────────────────────────────
export type FileBrowserEntryType = 'file' | 'directory' | 'symlink' | 'other';

export interface FileBrowserEntry {
  name: string;
  /** POSIX path relative to data-files root. Empty string = root. */
  path: string;
  type: FileBrowserEntryType;
  size: number;
  modifiedAt: string;
  createdAt: string;
  extension: string;
  hidden: boolean;
  /** For directories: count of immediate children. Null otherwise. */
  childCount: number | null;
}

export interface FileBrowserDirListing {
  path: string;
  breadcrumbs: Array<{ name: string; path: string }>;
  entries: FileBrowserEntry[];
  summary: { fileCount: number; dirCount: number; totalSize: number };
}

export interface FileBrowserStat {
  name: string;
  path: string;
  parentPath: string;
  type: FileBrowserEntryType;
  size: number;
  modifiedAt: string;
  createdAt: string;
  accessedAt: string;
  extension: string;
  mode: number;
  sha256: string | null;
  sha256Note: string | null;
  /** For directories: count of immediate children. Null otherwise. */
  childCount: number | null;
}

export const api = new ApiClient();

// ── Compass Slack bot (CO-460) ──
/** A Claude model an admin can choose (GET /api/admin/ai/model). */
export interface AIModelOption {
  id: string;
  displayName: string;
  summary: string;
  bedrockModelId: string;
  contextWindow: number;
  maxOutputTokens: number;
  defaultEffort: string;
  pricing: {
    inputPerMTok: number;
    outputPerMTok: number;
    cacheReadPerMTok: number;
    cacheWritePerMTok: number;
    longPromptThreshold?: number;
    longPrompt?: { inputPerMTok: number; outputPerMTok: number };
  };
  alwaysThinks: boolean;
  longContextTestCostUsd: number;
}

export interface AIModelSettings {
  selectedModelId: string;
  source: 'admin' | 'default';
  defaultModelId: string;
  models: AIModelOption[];
  tests: Array<{ id: string; name: string; description: string; optIn: boolean }>;
}

export interface SlackBotSettings {
  dryRun: boolean;
  allowedUserIds: string[];
  apiAppId: string;
  teamAllowList: string[];
  dmOnly: boolean;
  perUserHourlyLimit: number;
  denyMessageOverride?: string;
}

export interface SlackTranscriptRow {
  id: string;
  createdAt: string;
  slackUserId: string;
  user: { id: string; email: string | null; displayName: string | null } | null;
  channelType: string;
  channelId: string;
  resolvedAccountSfId: string | null;
  scopeDecision: string;
  inputText: string;
  outputText: string;
  /** Block Kit blocks of the reply (null for plain-text denials) — rendered in
   *  the viewer with the same engine the user saw in Slack. */
  outputBlocks: unknown[] | null;
  tokensIn: number;
  tokensOut: number;
  aiFeature: string | null;
  /** True for admin-simulator preview runs (hidden from the default view). */
  simulated: boolean;
}

export interface SlackTranscriptQuery {
  q?: string;
  userId?: string;
  slackUserId?: string;
  accountSfId?: string;
  outcome?: string;
  since?: string;
  until?: string;
  /** Include simulator preview runs alongside real traffic (default false). */
  includeSimulated?: boolean;
  limit?: number;
  offset?: number;
}

export interface ScoutCitation {
  n: number;
  title: string;
  url: string;
  accessedAt: string;
}

export interface ScoutRelationshipFacts {
  openAcv?: number;
  openOppCount?: number;
  commitAcv?: number;
  riskFlagCount?: number;
  contactCount?: number;
}

export interface ScoutBrief {
  outcome: 'ok' | 'low_confidence' | 'no_sources' | 'denied' | 'error';
  subjectType: 'person' | 'company';
  /** Bare resolved name (no title/company tail) — the profile header title. */
  subjectName: string;
  /** Enriched display name (folds in title/company). */
  resolvedName: string | null;
  /** The rendered brief (Markdown). Empty for no_sources/denied/error — see `message`. */
  text: string;
  message?: string;
  citations: ScoutCitation[];
  /** CRM context used (Contact-facts column). */
  context?: { company?: string; title?: string; email?: string; champion?: boolean };
  /** Scoped relationship to us (From-Compass column). */
  relationship?: { inScope: boolean; accountSfId: string | null; accountName: string | null; facts?: ScoutRelationshipFacts };
  relatedAccountSfId: string | null;
  usage: { input_tokens?: number; output_tokens?: number };
  model: string | null;
  /** Persisted run id (when the run-log write succeeded) — used to Save. */
  runId?: string | null;
  /** True when this brief was served from the cache (shows the Refresh button). */
  cached?: boolean;
  /** When the cached brief was first compiled (ISO) — for the "Cached …" label. */
  cachedAt?: string;
}

export interface ScoutHistoryRow {
  id: string;
  createdAt: string;
  subjectType: 'person' | 'company';
  subjectQuery: string;
  resolvedName: string | null;
  outcome: string;
  saved: boolean;
  relatedAccountSfId: string | null;
  briefText: string | null;
  citations: ScoutCitation[] | null;
}

// ── Recents (docs/Features/Recent) ──

/** One entry of a per-user "recently opened" list. `key` is the item's stable
 *  id within its kind (account sfId, snowflake accountKey, scout subject key);
 *  `meta` carries kind-specific extras (region/aeName, scout runId/outcome). */
export interface RecentItem {
  kind: string;
  key: string;
  label: string;
  meta: Record<string, unknown> | null;
  viewedAt: string;
}

export interface OkfDocView {
  id: string;
  type: 'glossary' | 'risk_flag' | 'checkpoint' | 'metric';
  slug: string;
  title: string;
  body: string;
  generatedBody: string | null;
  overrideBody: string | null;
  isOverridden: boolean;
  regenAt: string | null;
  updatedAt: string;
}

/** One row of the admin run log. Another user's run is metadata only: `private: true` and the subject, resolved name, account, brief and citations are null (Personal Space is private to its owner). */
export interface ScoutRunRow extends Omit<ScoutHistoryRow, 'subjectQuery'> {
  subjectQuery: string | null;
  user: { id: string; email: string | null; displayName: string | null } | null;
  sourcesCount: number;
  tokensIn: number;
  tokensOut: number;
  private?: boolean;
}

export interface ScoutConfigOverride {
  systemPrompt?: string;
  sectionPrompts?: Record<string, string>;
  model?: string | null;
  maxTokens?: number;
  sources?: { web: boolean };
}

export interface ScoutResolvedConfig {
  systemPrompt: string;
  sections: Record<string, string>;
  model: string | null;
  maxTokens: number;
  sources: { web: boolean };
}

export interface SlackSimulateResult {
  reply: { text: string; blocks: unknown[] };
  scopeDecision?: string;
  deliveryMode?: string;
  resolvedAccountSfId?: string | null;
  tokensIn?: number;
  tokensOut?: number;
  /** Present when the run produced a PDF (export path) — the simulator shows a file
   *  card; `dataBase64` is the actual rendered PDF so the admin can open/save it. */
  file?: { filename: string; bytes: number; comment?: string; dataBase64: string };
  ranAs: { userId: string; scope: 'bypass' | 'scoped' | 'unmapped' };
  /** Present when the simulate request asked to grade the reply (Phase F3). */
  eval?: AiEvalResult | null;
}

// ── AI Quality Console (docs/AiQuality) ──
export interface AiEvalCheck {
  label: string;
  status: 'pass' | 'fail' | 'warn' | 'na';
  detail?: string;
}
export interface AiEvalProvenance {
  ok: boolean;
  checks: AiEvalCheck[];
  ungroundedMoney: string[];
}
export interface AiEvalJudge {
  faithfulness: number;
  relevance: number;
  completeness: number;
  rationale: string;
}
export interface AiEvalResult {
  provenance: AiEvalProvenance;
  judge: AiEvalJudge | null;
  judgeError?: string;
}
export interface AiQualitySimulatedAs {
  userId: string;
  email: string;
  displayName: string;
}

// ── Assistant Simulators (docs/AiQuality Phase F) ──
export interface CompassAiSimResult {
  registered: boolean;
  answer: string | null;
  toolTrace: Array<{ name: string; phase: 'start' | 'end'; ok?: boolean }>;
  grounding: Array<{ kind: 'knowledge' | 'tool'; name?: string; content: string }>;
  eval: AiEvalResult | null;
  simulatedAs: AiQualitySimulatedAs | null;
}
export interface AssistantCompareDelta {
  faithfulness: number | null;
  relevance: number | null;
  completeness: number | null;
  groundedBoth: boolean;
  moneyAgreement: boolean;
}
export interface AssistantCompareResult {
  registered: boolean;
  simulatedAs: AiQualitySimulatedAs | null;
  web: {
    answer: string;
    toolTrace: Array<{ name: string; phase: 'start' | 'end'; ok?: boolean }>;
    grounding: Array<{ kind: 'knowledge' | 'tool'; name?: string; content: string }>;
    eval: AiEvalResult;
  } | null;
  slack: {
    reply: string | null;
    blocks: unknown[];
    scopeDecision: string | null;
    deliveryMode: string | null;
    resolvedAccountSfId: string | null;
    grounding: string | null;
    eval: AiEvalResult | null;
  } | null;
  delta: AssistantCompareDelta | null;
}

// ── Server resources (Admin → System Health → Server) ──────
// Mirrors src/admin/server-stats.ts. Byte counts are exact bytes; the history
// series is in whole megabytes (see the ServerMetricSample model comment).

export interface ServerMount {
  path: string;
  /** Every probed path that landed on this same filesystem. */
  paths: string[];
  label: string;
  totalBytes: number;
  freeBytes: number;
  usedBytes: number;
  usedPct: number;
}

export interface ServerDiskEntry {
  key: string;
  label: string;
  path: string;
  /** null when the path is absent or unreadable — `unavailable` says which. */
  bytes: number | null;
  description: string;
  unavailable?: string;
}

export interface ServerStats {
  host: {
    hostname: string;
    platform: string;
    nodeVersion: string;
    processUptimeSeconds: number;
    hostUptimeSeconds: number;
    cpu: { cores: number; model: string; load1: number; load5: number; load15: number };
    memory: {
      rssBytes: number;
      heapUsedBytes: number;
      heapTotalBytes: number;
      externalBytes: number;
      hostTotalBytes: number;
      hostFreeBytes: number;
      hostUsedBytes: number;
      hostUsedPct: number;
    };
  };
  mounts: ServerMount[];
  disk: {
    entries: ServerDiskEntry[];
    totalBytes: number;
    measuredAt: string;
    cached: boolean;
    durationMs: number;
  };
  generatedAt: string;
}

export interface ServerMetricPoint {
  t: string;
  rssMb: number;
  heapUsedMb: number;
  hostUsedMb: number;
  hostTotalMb: number;
  diskUsedMb: number | null;
  diskTotalMb: number | null;
  load1: number;
  uptimeSeconds: number;
}

export interface ServerMetricHistory {
  hours: number;
  sampleCount: number;
  /** >1 when the window was downsampled for the chart. */
  stride: number;
  points: ServerMetricPoint[];
  /** Timestamps where process uptime went backwards — i.e. a restart. */
  restarts: string[];
}

export interface ServerStorageTable {
  name: string;
  bytes: number;
  rows: number | null;
}

export interface ServerStorageSection {
  available: boolean;
  /** Why the section is unavailable. Shown verbatim — never rendered as "0". */
  reason?: string;
  totalBytes: number;
  tables: ServerStorageTable[];
  disks?: Array<{ name: string; path: string; totalBytes: number; freeBytes: number }>;
  databaseBytes?: number;
}

export interface ServerStorage {
  clickhouse: ServerStorageSection;
  postgres: ServerStorageSection;
  cached: boolean;
}
